// src/pages/OnboardingNominator.jsx
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import AppShell from "../layout/AppShell";
import "./Onboarding.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Country, State, City } from "country-state-city";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ENV, tbGetJson, tbPostJson, getJson } from "../api/client";
import { setDashboardToast } from "../utils/dashboardToast";
import { getCpErrorMessages, hasApiValidationFailure as hasCpValidationFailure } from "../utils/cpValidationErrors";
import { validateSecurityIdMmdd8 } from "../utils/securityId";
import { getLookupSecurityExistenceFromPayload, normalizeLookupSecurityExistence } from "../utils/lookupSecurity";
import {
    blockingLoadsForContinue,
    dataLoadToastId,
    loadFailMessage,
    loadOngoingMessage,
    loadTimeoutMessage
} from "../utils/onboardingDataLoadGate";
import { useDataLoadTimeoutToasts } from "../hooks/useDataLoadTimeoutToasts";
import { useDataLoadErrorToasts } from "../hooks/useDataLoadErrorToasts";

const FIRST_LAST_NAME_REGEX = /^(?=[a-zA-Z])[a-zA-Z.\-' ]{2,50}$/;


const name = sessionStorage.getItem("tb_name");
const email = sessionStorage.getItem("tb_email");
const approver = sessionStorage.getItem("approver");
const loggedInRole = (sessionStorage.getItem("tb_role") || "").trim();
const loggedInUserRole = loggedInRole;

const TB_BASE = (ENV.TB_BASE || "").replace(/\/+$/, "");
const ONBOARDING_DRAFT_URL =
    (ENV.ONBOARDING_DRAFT_URL || (TB_BASE && `${TB_BASE}/api/npw-onboarding-draft`)) || "";
const ONBOARDING_NEW_URL =
    (ENV.ONBOARDING_NEW_URL || (TB_BASE && `${TB_BASE}/api/npw-onboarding-new`)) || "";
const ONBOARDING_EDIT_URL =
    (ENV.ONBOARDING_EDIT_URL || (TB_BASE && `${TB_BASE}/api/npw-onboarding-edit`)) || "";
const CP_VALIDATE_URL =
    (ENV.CP_VALIDATE_URL ||
        ENV.CINGLEPOINT_URL ||
        ENV.NPW_CP_VALIDATE_URL ||
        ENV.ONBOARDING_CP_VALIDATE_URL ||
        (TB_BASE && `${TB_BASE}/api/v1/cinglepoint`) ||
        "") || "";
const APPROVAL_DETAILS_URL =
    (ENV.APPROVAL_DETAILS_URL || (TB_BASE && `${TB_BASE}/api/npw-approval-details`)) || "";
const TB_SEARCH_URL =
    (ENV.TB_SEARCH_URL || (TB_BASE && `${TB_BASE}/api/search`)) || "";
const SUPPLIER_VALIDATION_URL = (ENV.SUPPLIER_VALIDATION_URL ||
    "https://4ax3gccsbj.execute-api.us-west-2.amazonaws.com/dev/api/npw-s2p-supplier-validation").replace(/\/+$/, "");

const CP_COUNTRYLIST_URL = (ENV.CP_COUNTRYLIST_URL ||
    "https://gbc0othivl.execute-api.us-west-2.amazonaws.com/test/api/cp-countrylist").replace(/\/+$/, "");

const boolFromEnv = (v) =>
    v === true || v === "true" || v === "1" || v === 1 || v === "Y" || v === "y";

const truthy = (v) =>
    v === true || v === 1 || v === "1" || v === "true" || v === "t" || v === "T";

const generateTbFallbackDtvid = () => {
    const cryptoObj = typeof window !== "undefined" ? window.crypto : null;
    if (cryptoObj?.getRandomValues) {
        const values = new Uint32Array(1);
        cryptoObj.getRandomValues(values);
        return `TB-${String(values[0] % 10000).padStart(4, "0")}`;
    }
    return `TB-${String(Math.floor(Math.random() * 10000)).padStart(4, "0")}`;
};

const ENABLE_SPONSOR_VALIDATION = boolFromEnv(ENV.ENABLE_SPONSOR_VALIDATION);
const ENABLE_WEBPHONE_VALIDATION = boolFromEnv(ENV.ENABLE_WEBPHONE_VALIDATION);
const ENABLE_HINGE_ROLE_DROPDOWN = boolFromEnv(ENV.ENABLE_HINGE_ROLE_DROPDOWN);
const IS_VALIDATE_CP_NEEDED =
    (ENV.IS_VALIDATE_CP_NEEDED === undefined || ENV.IS_VALIDATE_CP_NEEDED === null || ENV.IS_VALIDATE_CP_NEEDED === "")
        ? true
        : boolFromEnv(ENV.IS_VALIDATE_CP_NEEDED);

// ──────────────────────────────────────────────────────────────
// EMAIL VALIDATION
// ──────────────────────────────────────────────────────────────
const EMAIL_MAX_LEN = 100;
// Strict-ish email validation with TLD capped to 2–6 (blocks obvious garbage like gmail.asdasdasda)
const EMAIL_REGEX = /^(?!\.)(?!.*\.\.)([A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*)@([A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,6}$/;


const HINGE_ROLE_LIST_URL = (ENV.HINGE_ROLE_LIST_URL ||
    "https://api-hingedev-directv.com/api/v1/roleCatalog").replace(/\/+$/, "");
const HINGE_SPONSOR_VALIDATE_URL = (ENV.HINGE_SPONSOR_VALIDATE_URL ||
    "https://api-hingedev-directv.com/api/v1/validation").replace(/\/+$/, "");
const HINGE_WEBPHONE_VALIDATE_URL = (ENV.HINGE_WEBPHONE_VALIDATE_URL ||
    "https://api-hingedev-directv.com/api/v1/validation").replace(/\/+$/, "");

// Prefer TeamBridge-backed Hinge proxy endpoints (tb-hinge) when provided via ENV.
// Falls back to the browser-facing Hinge URLs only if TB_* URLs are not configured.
const TB_HINGE_ROLE_LIST_URL = (ENV.TB_HINGE_ROLE_LIST_URL || ENV.TB_ROLE_CATALOG_URL || HINGE_ROLE_LIST_URL).replace(/\/+$/, "");
const TB_HINGE_SPONSOR_VALIDATE_URL = (ENV.TB_HINGE_SPONSOR_VALIDATE_URL || ENV.TB_HINGE_VALIDATE_URL || HINGE_SPONSOR_VALIDATE_URL).replace(/\/+$/, "");
const TB_HINGE_WEBPHONE_VALIDATE_URL = (ENV.TB_HINGE_WEBPHONE_VALIDATE_URL || ENV.TB_HINGE_VALIDATE_URL || HINGE_WEBPHONE_VALIDATE_URL).replace(/\/+$/, "");
const WEBPHONE_MANAGER_CACHE_KEY = "tb_webphone_manager_validation_v1";
const normalizeManagerCacheKey = (value) => String(value || "").trim().toLowerCase();
const getManagerValidationPayload = (resp) => {
    if (!resp || typeof resp !== "object") return resp;
    return (resp.response && typeof resp.response === "object")
        ? resp.response
        : (resp.data && typeof resp.data === "object")
            ? resp.data
            : resp;
};
const getManagerAttuidFromResp = (resp) => {
    const r = getManagerValidationPayload(resp);
    return String(r?.attuid ?? r?.attUid ?? r?.attUID ?? "").trim();
};
const getManagerIsValidFromResp = (resp) => {
    const r = getManagerValidationPayload(resp);
    const raw = r?.isValid ?? r?.isvalid ?? r?.valid;
    const normalized = String(raw ?? "").trim().toLowerCase();
    return raw === true || raw === 1 || ["true", "t", "yes", "y", "1"].includes(normalized);
};
const getManagerAttuidStatusRawFromResp = (resp) => {
    const r = getManagerValidationPayload(resp);
    return (
        r?.attuidStatus ??
        r?.attuidstatus ??
        r?.attuid_status ??
        r?.attUidStatus ??
        r?.attUIDStatus ??
        r?.attuidActive ??
        r?.attuid_active ??
        ""
    );
};
const isManagerAttuidActiveForAttuidFlow = (resp) => {
    const attuid = getManagerAttuidFromResp(resp);
    if (!attuid || ["false", "null", "undefined"].includes(attuid.toLowerCase())) return false;
    const rawStatus = getManagerAttuidStatusRawFromResp(resp);
    if (rawStatus === true || rawStatus === 1) return true;
    const status = String(rawStatus ?? "").trim().toLowerCase();
    return ["active", "true", "t", "yes", "y", "1"].includes(status);
};
const WEBPHONE_MANAGER_ATTUID_INACTIVE_MSG = "Webphone Manager ATTUID is Inactive for ATTUID flows, please check";
const readWebphoneManagerCache = (managerId) => {
    const key = normalizeManagerCacheKey(managerId);
    if (!key) return null;
    try {
        const raw = sessionStorage.getItem(WEBPHONE_MANAGER_CACHE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed?.[key] || null;
    } catch {
        return null;
    }
};
const writeWebphoneManagerCache = (managerId, response) => {
    const key = normalizeManagerCacheKey(managerId);
    if (!key) return;
    try {
        const raw = sessionStorage.getItem(WEBPHONE_MANAGER_CACHE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        const next = parsed && typeof parsed === "object" ? parsed : {};
        next[key] = { response };
        sessionStorage.setItem(WEBPHONE_MANAGER_CACHE_KEY, JSON.stringify(next));
    } catch {
        // ignore cache failures
    }
};


// ✅ NEW: TeamBridge “Hinge Search” API (proxy) URL from client.js ENV (same as Search.jsx)
const TB_HINGE_SEARCH_URL =
    (ENV.TB_HINGE_SEARCH_URL || ENV.TB_HINGE_PERSON_SEARCH_URL || ENV.TB_HINGE_SEARCH_PROXY_URL || "").replace(/\/+$/, "");

// For SSN/securityId existence check (min digits before we call Hinge Search)
const HINGE_SSN_MIN_DIGITS = Number(ENV.HINGE_SSN_MIN_DIGITS || 8);

const ENABLE_DTV_PREFILL = (ENV.ENABLE_DTV_PREFILL === undefined || ENV.ENABLE_DTV_PREFILL === null || ENV.ENABLE_DTV_PREFILL === "")
    ? true
    : boolFromEnv(ENV.ENABLE_DTV_PREFILL);
const HINGE_DTV_LOOKUP_URL = (
    (ENV.TB_HINGE_PERSON_LOOKUP_URL || ENV.TB_HINGE_LOOKUP_URL || ENV.LOOKUP_URL || ENV.HINGE_BASE || "")
).toString().trim().replace(/\/+$/, "");

const STATIC_DROPDOWNS = {
    validateSecurity: ["Yes", "No"],
    nonBillable: ["Yes", "No"],
    occupancyCode: ["virtual", "office"],
    workerTypeTax: ["W-2", "1099", "International"],//["W2 / 1099", "International"],
    workerPrimaryContact: ["Father", "Mother", "Spouse"],
    //roles: ["Developer", "Analyst", "Project Manager", "QA Engineer"],
    roles: ["Please Wait ... "],
    sites: ["Dallas", "Los Angeles", "New York", "Remote"],
    locations: ["TX-DAL-01", "CA-LA-03", "NY-NYC-02", "Remote"],
    npwTypes: ["Contractor", "Consultant", "Intern", "Vendor Staff"]
};

const getTimeZones = () => {
    const timeZones = Intl.supportedValuesOf("timeZone");
    const now = new Date();
    return timeZones.map((tz) => {
        const offset = new Intl.DateTimeFormat("en-US", {
            timeZone: tz,
            timeZoneName: "shortOffset",
            hour: "2-digit",
            minute: "2-digit"
        })
            .formatToParts(now)
            .find((p) => p.type === "timeZoneName")?.value;
        return { label: `(UTC${offset}) ${tz}`, value: tz };
    });
};
const timeZoneOptions = getTimeZones();

const NonMandatory = [
    "displayWorkforceToBuyer",
    "upload",
    "workstation",
    "billRate",
    "boltApproval",
    "username",
    "supplierReference",
    "autoRegister",
    "autoInvoiceAll",
    "timeSheetType",
    "contractPo",
    "poNum",
    "lineNum",
    "phone",
];

const FIELD_LABELS = {
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email",
    phone: "Phone",
    citizenship: "Country of Citizenship",
    sponsor: "Sponsor",
    webphoneManagerId: "Webphone Manager",
    role: "Role",
    country: "Country",
    state: "State",
    city: "City",
    timeZone: "Time Zone",
    occupancyCode: "Occupancy",
    securityId: "Security ID",
    validateSecurity: "Confirm Security ID",
    startDate: "Start Date",
    endDate: "End Date",
    workerTypeTax: "W2 / 1099 or International",
    nonBillable: "Billable",
    attRoleKey: "ATT Role"
};

const DTVUID_MAX_LEN = 10;
const CONTRACT_FIELDS_MAX_LEN = 50;

const requiredError = (name) => {
    const label = FIELD_LABELS[name] || name;
    return `Please enter ${label}`;
};

async function apiSaveDraft(payload) {
    if (!ONBOARDING_DRAFT_URL) throw new Error("ONBOARDING_DRAFT_URL not configured");
    return tbPostJson(ONBOARDING_DRAFT_URL, payload);
}
async function apiSubmitNew(payload) {
    if (!ONBOARDING_NEW_URL) throw new Error("ONBOARDING_NEW_URL not configured");
    return tbPostJson(ONBOARDING_NEW_URL, payload);
}
async function apiSubmitEdit(payload) {
    if (!ONBOARDING_EDIT_URL) throw new Error("ONBOARDING_EDIT_URL not configured");
    return tbPostJson(ONBOARDING_EDIT_URL, payload, { fetchInit: { method: "POST" } });
}
async function apiValidateCp(payload) {
    if (!CP_VALIDATE_URL) throw new Error("CP_VALIDATE_URL not configured");
    return tbPostJson(CP_VALIDATE_URL, payload, { fetchInit: { method: "POST" } });
}
async function apiFetchOnboardingById(id) {
    /*TODO:pentest*/
    // Build v query
    const dtvidRaw = sessionStorage.getItem("tb_dtvid") || "";
    const roleRaw = sessionStorage.getItem("tb_role") || sessionStorage.getItem("tb_role_raw") || "";
    const ts = Date.now();
    const vRaw = `${dtvidRaw}_*v*_${ts}`;
    const vParam = typeof btoa === "function" ? btoa(vRaw) : window.btoa(vRaw);

    if (!APPROVAL_DETAILS_URL) throw new Error("APPROVAL_DETAILS_URL not configured");
    const base = APPROVAL_DETAILS_URL;
    const sep = base.includes("?") ? "&" : "?";
    /*TODO:pentest*/ const url = `${base}${sep}npw_id=${encodeURIComponent(id)}&role=${encodeURIComponent(roleRaw)}&v=${encodeURIComponent(vParam)}&_=${Date.now()}`;
    // const url = `${base}${sep}npw_id=${encodeURIComponent(id)}`; //TODO:pentest comment this and uncomment above
    return tbGetJson(url);
}

const fmtDate = (date) => {
    let d = date instanceof Date ? date : new Date(date);
    return d instanceof Date && !isNaN(+d)
        ? d.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric"
        })
        : "";
};

const serializeDate = (d) => {
    if (!d) return null;
    const parsed = d instanceof Date ? d : new Date(d);
    if (!(parsed instanceof Date) || Number.isNaN(+parsed)) return null;
    const dt = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

const collectApiMessages = (value, out = [], depth = 0) => {
    if (depth > 4 || value == null) return out;
    if (typeof value === "string") {
        const msg = value.trim();
        if (msg) out.push(msg);
        return out;
    }
    if (Array.isArray(value)) {
        value.forEach((item) => collectApiMessages(item, out, depth + 1));
        return out;
    }
    if (typeof value === "object") {
        const keys = [
            "message",
            "error",
            "errorMessage",
            "detail",
            "reason",
            "errors",
            "validationErrors",
            "fieldErrors",
            "results",
            "body",
            "agentBulkResults",
            "messages",
        ];
        keys.forEach((k) => collectApiMessages(value?.[k], out, depth + 1));
    }
    return out;
};

const hasApiValidationFailure = (resp) => {
    return hasCpValidationFailure(resp);
};


/* =========================
   SECURITY ID MASKING RULES
   ========================= */

const EyeIcon = ({ size = 18 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

const isUnitedStatesSelected = (countryLabel = "") => {
    const c = String(countryLabel || "").trim().toLowerCase();
    return (
        c === "united states" ||
        c === "united states of america" ||
        c === "usa" ||
        c === "us"
    );
};

// For US: ___-__-____ (9 digits)
const US_TEMPLATE = "   -  -    ";
const US_DIGIT_POS = [0, 1, 2, 4, 5, 7, 8, 9, 10];

// For Non-US: f-__-__-____ (8 digits)
const NONUS_TEMPLATE = "f-  -  -    ";
const NONUS_DIGIT_POS = [2, 3, 5, 6, 8, 9, 10, 11];

function templateFor(isUS) {
    return isUS ? US_TEMPLATE : NONUS_TEMPLATE;
}
function digitPosFor(isUS) {
    return isUS ? US_DIGIT_POS : NONUS_DIGIT_POS;
}
function maxDigitsFor(isUS) {
    return isUS ? 9 : 8;
}

function extractDigits(s, isUS) {
    const str = String(s || "");
    if (isUS) {
        // Only digits allowed for US
        return str.replace(/\D/g, "");
    }
    if (isUS === false) {
        // For non-US: first 4 must be digits, last 4 can be alphanumeric
        let out = "";
        for (let i = 0; i < str.length && out.length < 8; i++) {
            const ch = str[i];
            if (out.length < 4) {
                if (/\d/.test(ch)) {
                    out += ch;
                }
            } else {
                if (/\w/.test(ch)) {
                    out += ch;
                }
            }
        }
        return out?.toUpperCase();
    } else {
        return str.replace(/\D/g, "");
    }
}

function clampDigitsToFormat(isUS, digits) {
    const max = maxDigitsFor(isUS);
    return String(digits || "").slice(0, max);
}

// Heuristic: allow routes like "/onboarding/new/dt0126.uat" (plain DTVID without "DTVID=")
// to behave the same as "/onboarding/new/DTVID%3Ddt0126.uat".
function looksLikeDtvId(v) {
    const s = String(v || "").trim();
    if (!s) return false;
    if (s.toLowerCase() === "new") return false;
    // exclude explicit key=value forms (handled elsewhere)
    if (/^(?:DTVID|dtvid)\s*=/.test(s)) return false;
    // exclude pure numeric ids (npw_id)
    if (/^\d+$/.test(s)) return false;
    // common DTVID formats seen in TeamBridge (examples: dt0126.uat, dt115n)
    return /^dt[0-9a-z._-]+$/i.test(s) && s.length <= 64;
}

// Detect "/onboarding/new/DTVID=xxxx" routes (encoded as "DTVID%3Dxxxx")
function isDtvRouteId(routeId) {
    if (!routeId) return false;
    try {
        const decoded = decodeURIComponent(String(routeId));
        return /^DTVID=/i.test(decoded);
    } catch {
        return /^DTVID=/i.test(String(routeId));
    }
}
function getDtvIdFromRoute(routeId) {
    if (!routeId) return "";
    try {
        const decoded = decodeURIComponent(String(routeId));
        if (!/^DTVID=/i.test(decoded)) return "";
        return decoded.split("=", 2)[1] || "";
    } catch {
        const s = String(routeId);
        if (!/^DTVID=/i.test(s)) return "";
        return s.split("=", 2)[1] || "";
    }
}


// Convert digits entered => masked display that keeps separators (and 'f' for non-US)
function buildMaskedDisplay(isUS, digits) {
    const tpl = templateFor(isUS).split("");
    const pos = digitPosFor(isUS);
    const n = Math.min(digits.length, pos.length);
    for (let i = 0; i < pos.length; i++) {
        tpl[pos[i]] = i < n ? "*" : "_";
    }
    return tpl.join("");
}

// Show only last4 in review, rest masked
function buildMaskedLast4(isUS, digits) {
    const tpl = templateFor(isUS).split("");
    const pos = digitPosFor(isUS);
    const last4 = String(digits || "").slice(-4);
    const total = maxDigitsFor(isUS);

    for (let i = 0; i < pos.length; i++) tpl[pos[i]] = "*";

    const start = Math.max(0, total - 4);
    for (let i = start; i < total; i++) {
        const idxInLast4 = i - start;
        tpl[pos[i]] = last4[idxInLast4] || "*";
    }
    return tpl.join("");
}

// Build "unmasked" formatted value for eye toggle
function buildUnmaskedFormatted(isUS, digits) {
    const d = clampDigitsToFormat(isUS, extractDigits(digits, isUS));
    if (!d) return "";
    if (isUS) {
        const a = d.slice(0, 3);
        const b = d.slice(3, 5);
        const c = d.slice(5, 9);
        return `${a}${d.length > 3 ? "-" : ""}${b}${d.length > 5 ? "-" : ""}${c}`.replace(
            /-$/,
            ""
        );
    }
    const a = d.slice(0, 2);
    const b = d.slice(2, 4);
    const c = d.slice(4, 8);
    return `f-${a}${d.length > 2 ? "-" : ""}${b}${d.length > 4 ? "-" : ""}${c}`.replace(
        /-$/,
        ""
    );
}

function setCaretToNextSlot(inputEl, isUS, digitsLen) {
    if (!inputEl) return;
    const pos = digitPosFor(isUS);
    const nextIndex =
        pos[Math.min(digitsLen, pos.length - 1)] ??
        templateFor(isUS).length;
    const caret =
        digitsLen >= pos.length ? templateFor(isUS).length : nextIndex;
    try {
        inputEl.setSelectionRange(caret, caret);
    } catch { }
}

function setCaretToSlotIndex(inputEl, idx) {
    if (!inputEl) return;
    try {
        inputEl.setSelectionRange(idx, idx);
    } catch { }
}


// Prefer plaintext/decrypted Security ID fields when present.
// Many APIs return masked values in `security_id` (e.g., "***-**-____"), which contains no digits.
// Eye-toggle requires digits to be available in state to render unmasked format.
function resolveSecurityDigits(api) {
    if (!api) return "";
    const candidates = [
        api.security_id_plaintext,
        api.securityIdPlaintext,
        api.security_id_unmasked,
        api.securityIdUnmasked,
        api.security_id_value,
        api.securityIdValue,
        api.security_id_raw,
        api.securityIdRaw,
        api.security_id,        // may be masked
        api.securityId,
        // Hinge DTV lookup can return only last-4 digits
        api.ssnLast4d,
        api.ssn_last4d,
        api.ssnLast4,
        api.ssn_last4,
        api.security_id_hash,   // not usable for display but may contain digits in some legacy payloads
    ];
    for (const v of candidates) {
        const d = extractDigits(v ?? "");
        if (d) return d;
    }
    return "";
}


/* =========================
   HINGE SEARCH (SSN existence check)
   - Uses the same TB_HINGE_SEARCH_URL as Search.jsx
   - Parses multiple possible response shapes from the proxy
   ========================= */
function normalizeHingePeople(payload) {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (typeof payload === "string") {
        try { return normalizeHingePeople(JSON.parse(payload)); } catch { return []; }
    }
    const wrapped = payload?.response && typeof payload.response === "object" ? payload.response : null;
    const p = wrapped || payload;
    return (
        p.personDetails ||
        p.personList ||
        p.personDetailsList ||
        p.data ||
        p.results ||
        p.items ||
        []
    );
}

function hingePersonFullName(p) {
    const first = String(p?.firstName ?? p?.first_name ?? "").trim();
    const last = String(p?.lastName ?? p?.last_name ?? "").trim();
    const full = `${first} ${last}`.trim();
    return full || "";
}

function mapApiToForm1(api) {
    if (!api) return null;
    const nonBillable =
        api.non_billable === true ? "Yes" : api.non_billable === false ? "No" : "";
    const attuidRequired =
        api.attuid_required === true ? "Yes" : api.attuid_required === false ? "No" : "No";
    const autoRegister =
        api.auto_register === true ? "Yes" : api.auto_register === false ? "No" : "";
    const autoInvoiceAll =
        api.auto_invoice_all_characteristics === true
            ? "Yes"
            : api.auto_invoice_all_characteristics === false
                ? "No"
                : "";
    const displayWorkforceToBuyer =
        api.display_candidate_to_buyer === true
            ? "Yes"
            : api.display_candidate_to_buyer === false
                ? "No"
                : "";
    const result = {
        status: api.status ?? "",
        isSecExist: normalizeLookupSecurityExistence(api.isSecExist),
        attuid: api.attuid ?? api.attUid ?? api.attUID ?? "",
        attuidRequired,
        site: api.site_id ?? "",
        location: api.location ?? "",
        firstName: api.first_name ?? "",
        lastName: api.last_name ?? "",
        email: api.email ?? "",
        phone: api.phone ?? "",
        securityId: resolveSecurityDigits(api),
        validateSecurity: resolveSecurityDigits(api),
        startDate: api.start_date ? new Date(api.start_date) : null,
        endDate: api.end_date ? new Date(api.end_date) : null,
        autoRegister,
        timeZone: api.time_zone ?? "",
        department: api.department ?? "DIRECT (EGA003)",
        webphoneManagerId: api.webphone_manager ?? "",
        webphone_manager_attuid: api.webphone_manager_attuid ?? api.webphoneManagerAttuid ?? "",
        webphoneManagerEmail: api.webphone_manager_email ?? api.webphoneManagerEmail ?? "",
        webphone_manager_email: api.webphone_manager_email ?? api.webphoneManagerEmail ?? "",
        sponsor: api.sponsor ?? "",
        sponsorEmail: api.sponsor_email ?? api.sponsorEmail ?? "",
        sponsor_email: api.sponsor_email ?? api.sponsorEmail ?? "",
        npwType: api.employment_type ?? api.npw_type ?? api?.personType ?? "Non-staff Supp Worker",
        workerTypeTax: api.worker_type_tax ?? "",
        workerPrimaryContact: api.worker_primary_contact ?? "",
        nonBillable,
        country:
            api.country ??
            api.countryOfWorking ??
            api.country_of_working ??
            api.countryOfWork ??
            "",
        state: api.state ?? api.stateOfWorking ?? api.state_of_working ?? "",
        city: api.city ?? api.cityOfWorking ?? api.city_of_working ?? "",
        occupancyCode: api.occupancy ?? api.occupation_indicator_code ?? "",
        workstation: api.floor_workstation_cubicle ?? "",
        supplierReference: api.supplier_reference ?? "",
        vendor: api.vendor ?? "",
        contractPo: api.contract_po ?? api.contract ?? "",
        poNum: api.po_number ?? "",
        lineNum: api.line_number ?? "",
        billRate: api.bill_rate ?? "",
        boltApproval: api.bolt_approval_number ?? "",
        npwManagerCompany: api.npw_manager_company ?? "",
        role: api.role ?? "",
        citizenship: api.citizenship ?? "",
        autoInvoiceAll,
        username: api.username ?? "",
        displayWorkforceToBuyer,
        timeSheetType: api.timesheet_type ?? "No Time Sheets",
        dtvId: api.dtv_id ?? "",
        attRoleKey: api.att_role_cp_role_id ?? api.attRoleKey ?? "",
        attRoleDtvRoleId: api.attRoleDtvRoleId ?? "",
        attRoleDtvRoleName: api.attRoleDtvRoleName ?? "",
        attRoleCpRoleId: api.att_role_cp_role_id ?? api.attRoleCpRoleId ?? "",
        attRoleCpRoleDesc: api.att_role_cp_role_desc ?? api.attRoleCpRoleDesc ?? "",
        attRoleCpProfileId: api.att_role_cp_profile_id ?? api.attRoleCpProfileId ?? "",
        attRoleCpProfileDesc: api.att_role_cp_profile_desc ?? api.attRoleCpProfileDesc ?? "",
        npwTypeRaw: api.npw_type ?? null
    };
    return result;
}

export default function OnboardingNominator({ npwId = null, backToMain = null }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();

    // Robust "Back" behavior for edit routes:
    // 1) use provided backToMain callback (if embedded)
    // 2) use location.state.from (if caller passed it)
    // 3) fall back to shared Search/Overview returnPath sessionStorage
    // 4) fall back to browser history
    // 5) finally fall back to dashboard/overview
    const handleBackToCaller = () => {
        try {
            if (typeof backToMain === "function") {
                backToMain();
                return;
            }
        } catch { /* ignore */ }

        const from = typeof location?.state?.from === "string" ? location.state.from : "";
        if (from) {
            navigate(from, { replace: true });
            return;
        }

        try {
            const host = (sessionStorage.getItem("npw.search.host") || "").trim();
            const ret = (sessionStorage.getItem("npw.search.returnPath") || "").trim();
            const target = host || ret;
            if (target) {
                navigate(target, { replace: true });
                return;
            }
        } catch { /* ignore */ }

        try {
            if (window.history.length > 1) {
                navigate(-1);
                return;
            }
        } catch { /* ignore */ }

        navigate(OVERVIEW_PATH, { replace: true });
    };

    const hasExternalCallerTarget = (() => {
        const from = typeof location?.state?.from === "string" ? location.state.from.trim() : "";
        let host = "";
        let ret = "";
        try {
            host = (sessionStorage.getItem("npw.search.host") || "").trim();
            ret = (sessionStorage.getItem("npw.search.returnPath") || "").trim();
        } catch { /* ignore */ }
        const target = (from || host || ret || "").trim();
        return Boolean(target && (/^\/(approvals|manage-requests)(\b|\/|\?)/.test(target)));
    })();

    /* ------------------------------------------------------------------ */
    /*  TOAST + NAV HELPERS                                               */
    /* ------------------------------------------------------------------ */
    const OVERVIEW_PATH = (ENV.OVERVIEW_PATH || ENV.OVERVIEW_ROUTE || "/dashboard");

    const toastThenNavigate = (message, type = "success", path = OVERVIEW_PATH) => {
        const msg = String(message || "").trim();
        if (!msg) {
            navigate(path, { replace: true });
            return;
        }

        // Persist for the destination page (if it renders a dashboard toast)
        try {
            setDashboardToast(msg, type);
        } catch { /* non-blocking */ }

        // IMPORTANT: ToastContainer is inside this page, so navigating immediately
        // will unmount it before the toast becomes visible.
        const show =
            type === "success" ? toast.success :
                type === "warn" ? toast.warn :
                    type === "info" ? toast.info : toast.error;

        show(msg, {
            autoClose: 1200,
            closeOnClick: true,
            pauseOnHover: true,
            onClose: () => navigate(path, { replace: true })
        });
    };

    // Match EmpSupplierAdmin behavior: support dtvId/dtvid passed via query-string or location.state.
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const isReactiveAttuidFlow = useMemo(() => {
        const flag = (searchParams.get("reactivate_attuid") || searchParams.get("reactive_attuid") || "").trim();
        return truthy(flag) || truthy(location?.state?.reactivateAttuid);
    }, [searchParams, location.state]);
    const dtvIdFromQueryOrState = useMemo(() => {
        const q = (searchParams.get("dtvId") || searchParams.get("dtvid") || "").trim();
        const s = (location.state?.dtvId || "").trim();
        return q || s || "";
    }, [searchParams, location.state]);

    // Route param supports both:
    // 1) /onboarding/new/DTVID%3Dxxxx  -> prefill from Hinge/Lookup
    // 2) /onboarding/new/<npw_id>      -> load draft/details from Approval Details API
    const routeToken = useMemo(() => {
        const v = (id || "").toString().trim();
        if (!v) return "";
        try {
            return decodeURIComponent(v);
        } catch {
            return v;
        }
    }, [id]);

    const routeDtvid = useMemo(() => {
        const t = (routeToken || "").trim();
        const m = /^(?:DTVID|dtvid)\s*=\s*(.+)$/i.exec(t);
        return m ? String(m[1] || "").trim() : "";
    }, [routeToken]);

    // Allow bare dtvId in the route token ("/onboarding/new/dt0126.uat")
    const routeBareDtvid = useMemo(() => {
        const t = (routeToken || "").trim();
        return looksLikeDtvId(t) ? t : "";
    }, [routeToken]);

    // Single authoritative dtvId value for this page.
    // Priority: explicit DTVID= token > query/state > bare token.
    const effectiveDtvid = useMemo(() => {
        return (
            (routeDtvid || "").trim() ||
            (dtvIdFromQueryOrState || "").trim() ||
            (routeBareDtvid || "").trim() ||
            ""
        );
    }, [routeDtvid, dtvIdFromQueryOrState, routeBareDtvid]);

    const routeNpwId = useMemo(() => {
        const v = (routeToken || "").trim();
        if (!v) return null;
        if (v.toLowerCase() === "new") return null;
        if (/^(?:DTVID|dtvid)\s*=/.test(v)) return null;
        // If token is actually a DTVID, do NOT treat as NPW id.
        if (looksLikeDtvId(v)) return null;
        return v;
    }, [routeToken]);

    const isNumericNpwId = (v) => /^\d+$/.test(String(v ?? "").trim());

    const effectiveNpwId = useMemo(() => {
        const p = npwId != null && String(npwId).trim() ? String(npwId).trim() : null;
        const candidate = p || routeNpwId;
        // Only treat numeric values as NPW IDs. Anything else (ex: "DTVID=pt3665.uat") must NOT be sent as npw_id.
        return isNumericNpwId(candidate) ? String(candidate).trim() : null;
    }, [npwId, routeNpwId]);

    // Per UX: treat as edit ONLY when we have a numeric NPW id.
    // DTVID=... tokens are prefill-only and must remain "New".
    const isEditRoute = Boolean(effectiveNpwId);
    const [editData, setEditData] = useState(null);

    const editHeadingOverride = useMemo(() => {
        if (!isEditRoute) return "";
        const statusKey = (s) => String(s || "").trim().toLowerCase().replace(/[\s_-]/g, "");
        const k = statusKey(editData?.personStatus ?? editData?.person_status ?? editData?.status);
        if (k === "inactive") return "Rehire Onboarding";
        if (k === "active") return "Modify Onboarding";
        return "";
    }, [isEditRoute, editData?.status, editData?.personStatus, editData?.person_status]);

    // Security ID edit/prefill context (NPW id OR DTVID prefill routes)
    const isSecurityEditContext = Boolean((effectiveNpwId && String(effectiveNpwId).trim()) || (effectiveDtvid && String(effectiveDtvid).trim()));

    const [dd, setDd] = useState(STATIC_DROPDOWNS);
    const workerType = "Non-Payroll Worker"; // TeamBridge always onboards NPW

    const [step, setStep] = useState(1);


    // Security ID: in edit/prefill flows, inputs remain read-only until user clicks "Edit"
    const [securityEditEnabled, setSecurityEditEnabled] = useState(false);
    const [securityInitialDigits, setSecurityInitialDigits] = useState("");
    const securityInitCapturedRef = useRef(false);
    const securityInitialIsUSRef = useRef(null);

    // DTV-prefill: treat Active/Terminated people as an "edit-like" context for Security ID.
    const [dtvPrefillExistingRecord, setDtvPrefillExistingRecord] = useState(false);
    const [dtvPrefillPersonStatus, setDtvPrefillPersonStatus] = useState("");
    const [lookupAttuid, setLookupAttuid] = useState("");
    const [lookupSecExistFlag, setLookupSecExistFlag] = useState("");

    const dtvPrefillHeadingOverride = useMemo(() => {
        // NEW onboarding route (prefill via hinge lookup): drive heading from lookup personStatus
        if (isEditRoute) return "";
        if (!dtvPrefillExistingRecord) return "";
        const statusKey = (s) => String(s || "").trim().toLowerCase().replace(/[\s_-]/g, "");
        const k = statusKey(dtvPrefillPersonStatus);
        if (k === "inactive") return "Rehire Onboarding";
        if (k === "active") return "Modify Onboarding";
        return "";
    }, [isEditRoute, dtvPrefillExistingRecord, dtvPrefillPersonStatus]);

    useEffect(() => {
        // Reset when leaving edit/prefill context
        if (!isSecurityEditContext) {
            securityInitCapturedRef.current = false;
            setSecurityEditEnabled(false);
            setSecurityInitialDigits("");
            securityInitialIsUSRef.current = null;
            setDtvPrefillExistingRecord(false);
            setDtvPrefillPersonStatus("");
            setLookupSecExistFlag("");
            return;
        }

        const lookupSecExistKnown = lookupSecExistFlag === "yes" || lookupSecExistFlag === "no";
        if (!lookupSecExistKnown) return;

        // Capture initial security value once we have loaded an existing record.
        if (securityInitCapturedRef.current) return;
        if (lookupSecExistFlag !== "yes") {
            securityInitCapturedRef.current = true;
            setSecurityInitialDigits("");
            securityInitialIsUSRef.current = null;
            return;
        }
        if (!editData) return;

        securityInitCapturedRef.current = true;
        setSecurityInitialDigits(extractDigits(editData.securityId || ""));

        // Capture the rule used at prefill time so if Country/Attuid Required changes while locked,
        // we can force re-entry once the user clicks Edit.
        const editAttuidOn =
            editData.attuidRequired === "Yes" ||
            editData.attuidRequired === "true" ||
            editData.isAttuidRequested === "Yes" ||
            editData.isAttuidRequested === true;
        securityInitialIsUSRef.current = editAttuidOn && isUnitedStatesSelected(editData.country);
    }, [isSecurityEditContext, editData, lookupSecExistFlag]);

    const securityInitialHadValue = Boolean(securityInitialDigits);
    const hasLookupSecurity = lookupSecExistFlag === "yes";
    // Show Security ID edit controls ONLY when editing an existing record (not create / prefill).
    // In edit flows, Security ID/SSN should be non-editable by default until user clicks "Edit".
    const isSecurityEditRecord = Boolean((isEditRoute || dtvPrefillExistingRecord) && hasLookupSecurity);
    const isSecurityLocked = isSecurityEditRecord && !securityEditEnabled;
    const showSecurityEditButton = isSecurityEditRecord && !securityEditEnabled;
    const showSecurityDisableEditButton = isSecurityEditRecord && securityEditEnabled;

    /**
     * Track draft IDs returned from Draft API so Submit can route to EDIT (PUT)
     */
    const [draftNpwId, setDraftNpwId] = useState(() => {
        const v = sessionStorage.getItem("tb_draft_npw_id");
        const t = v ? String(v).trim() : "";
        // Only persist numeric NPW IDs; ignore prefill tokens like "DTVID=..." that break bigint parsing downstream.
        return /^\d+$/.test(t) ? t : null;
    });
    const [draftClientRequestId, setDraftClientRequestId] = useState(() => {
        const v = sessionStorage.getItem("tb_draft_client_request_id");
        return v ? String(v) : null;
    });
    const [hingeRoleCatalog, setHingeRoleCatalog] = useState([]);
    const [loginVendorName, setLoginVendorName] = useState("");
    const [countryOptions, setCountryOptions] = useState([]);
    const [citizenshipOptions, setCitizenshipOptions] = useState([]); // from CP countrylist API
    const [stateOptions, setStateOptions] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [ctzn, setCitizen] = useState(null);
    const [selectedState, setSelectedState] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const isStateRequired = Boolean(selectedCountry && stateOptions.length > 0);
    const isCityRequired = Boolean(isStateRequired && selectedState && cityOptions.length > 0);

    // When we programmatically set geo fields (e.g., Hinge Active prefill),
    // avoid the country/state cascades wiping state/city immediately after render.
    const suppressCountryCascadeRef = useRef(false);
    const suppressStateCascadeRef = useRef(false);
    const [selectedTimeZone, SetSelectedTimeZone] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [cpValidation, setCpValidation] = useState({ status: "idle", message: "" });
    const [sponsorValidation, setSponsorValidation] = useState({ status: "idle", message: "" });
    const [webphoneValidation, setWebphoneValidation] = useState({ status: "idle", message: "" });
    const [contractValidation, setContractValidation] = useState({ status: "idle", message: "" });
    const [poValidation, setPoValidation] = useState({ status: "idle", message: "" });
    const [lineValidation, setLineValidation] = useState({ status: "idle", message: "" });


    // ✅ NEW: SSN/Security ID existence check via TB_HINGE_SEARCH_URL (debounced)
    const [ssnAssociation, setSsnAssociation] = useState({ status: "idle", message: "" }); // idle|checking|found|none|error
    const ssnDebounceRef = useRef(null);
    const ssnReqSeqRef = useRef(0);

    const [dataLoads, setDataLoads] = useState({
        roles: {
            field: "Role",
            // Only load/validate roles when ATTUID is requested.
            status: "idle",
            startedAt: null
        },
        vendorSupplier: { field: "Vendor/Supplier", status: "idle", startedAt: null }
    });

    useDataLoadTimeoutToasts(dataLoads, { timeoutMs: 20000 });
    useDataLoadErrorToasts(dataLoads);

    const [supplierLocked, setSupplierLocked] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    /* =========================
       API call de-dupe (React 18 StrictMode-safe)
       - Ensures the same API (keyed by a stable string) is only executed once per component lifetime.
       - If called again with the same key, the original Promise is returned.
       ========================= */
    const apiOnceRef = useRef(new Map()); // key -> Promise
    const apiInFlightRef = useRef(new Set()); // key -> boolean (for click handlers)

    const callApiOnce = (key, fn) => {
        if (!key) return Promise.resolve().then(fn);
        const m = apiOnceRef.current;
        if (m.has(key)) return m.get(key);
        const p = Promise.resolve().then(fn).catch((e) => {
            // On failure, allow a retry by removing the cached promise.
            m.delete(key);
            throw e;
        });
        m.set(key, p);
        return p;
    };

    const callInFlightOnce = async (key, fn) => {
        if (!key) return fn();
        const s = apiInFlightRef.current;
        if (s.has(key)) return undefined;
        s.add(key);
        try {
            return await fn();
        } finally {
            s.delete(key);
        }
    };

    useEffect(() => {
        if (!isEditRoute) return;
        if (!HINGE_DTV_LOOKUP_URL) return;

        const targetDtvId = String(editData?.dtvId || editData?.dtv_id || "").trim();
        if (!targetDtvId) return;

        let canceled = false;
        (async () => {
            try {
                const base = HINGE_DTV_LOOKUP_URL;
                const sep = base.includes("?") ? "&" : "?";
                const resp = await callApiOnce(
                    `workerSecExist:${targetDtvId}`,
                    () => tbGetJson(`${base}${sep}dtvId=${encodeURIComponent(targetDtvId)}`)
                );
                if (canceled) return;
                setLookupSecExistFlag(getLookupSecurityExistenceFromPayload(resp));
            } catch {
                if (!canceled) setLookupSecExistFlag("");
            }
        })();

        return () => {
            canceled = true;
        };
    }, [isEditRoute, editData?.dtvId, editData?.dtv_id]);






    const initialVal = {
        attuid: "",
        attuidRequired: "No",
        site: "",
        location: "",
        firstName: "",
        lastName: "",
        phone: "",
        role: "",
        securityId: "",
        validateSecurity: "",
        startDate: null,
        endDate: null,
        autoRegister: "Yes",
        timeZone: "",
        department: "DIRECT (EGA003)",
        webphoneManagerId: "",
        webphone_manager_attuid: "",
        webphoneManagerEmail: "",
        webphone_manager_email: "",
        npwType: "Non-staff Supp Worker",
        workstation: "",
        occupancyCode: "",
        workerPrimaryContact: "",
        nonBillable: "",
        sponsor: "",
        sponsorEmail: "",
        sponsor_email: "",
        contractPo: "",
        billRate: "",
        boltApproval: "",
        npwManagerCompany: "",
        workerTypeTax: "",
        citizenship: "",
        country: "",
        city: "",
        state: "",
        autoInvoiceAll: "No",
        supplierReference: "",
        username: "",
        displayWorkforceToBuyer: "",
        timeSheetType: "No Time Sheets",
        vendor: "Vendor Name",
        dtvId: "",
        poNum: "",
        lineNum: "",
        email: "",
        attRoleKey: "",
        attRoleDtvRoleId: "",
        attRoleDtvRoleName: "",
        attRoleCpRoleId: "",
        attRoleCpRoleDesc: "",
        attRoleCpProfileId: "",
        attRoleCpProfileDesc: ""
    };
    const [form1, setForm1] = useState(initialVal);

    /* Attuid required? (drives Security ID validation rule) */
    // NOTE: This is defined later in the file in the original code; avoid redeclaring here.

    /* -----------------------------
       Security ID masking + eye toggle
       - form1.securityId and form1.validateSecurity store DIGITS ONLY
       - UI renders masked format with fixed separators
       - backend payload sends digits only and omits validateSecurity
       ----------------------------- */
    const securityRef = useRef(null);
    const confirmSecurityRef = useRef(null);
    const securityTouchedRef = useRef(false);
    const confirmSecurityTouchedRef = useRef(false);

    // UI: Security ID input + message fixed width (per requirement)
    const SECURITY_INPUT_WIDTH_PX = 180;


    const [showSecurityId, setShowSecurityId] = useState(false);
    const [showConfirmSecurityId, setShowConfirmSecurityId] = useState(false);

    const attuidRequiredOn = (() => {
        const attuidRequiredNorm = String(form1.attuidRequired || "").trim().toLowerCase();
        const requestedRaw = form1.isAttuidRequested;
        const requestedNorm = String(requestedRaw || "").trim().toLowerCase();
        return (
            attuidRequiredNorm === "yes" ||
            attuidRequiredNorm === "true" ||
            requestedRaw === true ||
            requestedNorm === "yes" ||
            requestedNorm === "true"
        );
    })();
    const requiresCpValidateBeforeSubmit = IS_VALIDATE_CP_NEEDED && attuidRequiredOn;

    useEffect(() => {
        setCpValidation((prev) => (prev.status === "idle" ? prev : { status: "idle", message: "" }));
    }, [attuidRequiredOn, form1]);



    useEffect(() => {
        // When ATTUID Required is OFF:
        // - Hide Country of Citizenship + W2/1099
        // - Clear any previously selected values + related validation errors
        if (!attuidRequiredOn) {
            setCitizen(null);
            setForm1((prev) => ({
                ...prev,
                citizenship: "",
                workerTypeTax: ""
            }));
            setFieldErrors((prev) => ({
                ...prev,
                citizenship: "",
                workerTypeTax: ""
            }));
        }
    }, [attuidRequiredOn]);
    // Security ID rule (per requirement):
    // Apply US rule ONLY when (Attuid Required = Yes) AND (Country = United States).
    // Otherwise apply Non-US rule.
    const isUS = useMemo(
        () => attuidRequiredOn && isUnitedStatesSelected(form1.country),
        [attuidRequiredOn, form1.country]
    );

    const securityPlaceholder = useMemo(() => templateFor(isUS), [isUS]);
    const securityHint = useMemo(() => {
        return isUS ? "Enter SSN (numbers only, 9 digits). Format: 123-45-6789" : "Numbers only. Enter Security ID (8 digits). Format: f-mm-dd-xxxx (Birth Month-Date-Last 4 of Security ID).";
    }, [isUS]);

    const securityLabel = useMemo(() => (isUS ? "SSN" : "Security ID"), [isUS]);
    const confirmSecurityLabel = useMemo(() => (isUS ? "Confirm SSN" : "Confirm Security ID"), [isUS]);
    const securityTooltipText = useMemo(() => {
        return isUS
            ? "US + ATTUID Requested: enter SSN (9 digits)."
            : "Non-US or ATTUID not requested: enter Security ID as f-mm-dd-xxxx (Birth Month-Date-Last 4 of Sec ID).";
    }, [isUS]);


    const prevIsUSRef = useRef(null);

    useEffect(() => {
        if (isSecurityLocked) return;

        const prev = prevIsUSRef.current;
        prevIsUSRef.current = isUS;

        // Preserve values across rule changes; clamp if switching 9->8.
        if (prev !== null && prev !== isUS) {
            setForm1((p) => {
                const nextSecurityId = clampDigitsToFormat(isUS, extractDigits(p.securityId, isUS));
                const nextValidateSecurity = clampDigitsToFormat(isUS, extractDigits(p.validateSecurity, isUS));
                if (nextSecurityId === p.securityId && nextValidateSecurity === p.validateSecurity) return p;
                return { ...p, securityId: nextSecurityId, validateSecurity: nextValidateSecurity };
            });
            setShowSecurityId(false);
            setShowConfirmSecurityId(false);
        }

        // If user already typed something, revalidate under the new rule.
        if (form1.securityId || form1.validateSecurity) {
            if (form1.securityId) runFieldValidation("securityId", form1.securityId);
            if (form1.validateSecurity) runFieldValidation("validateSecurity", form1.validateSecurity);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUS, isSecurityLocked]);

    // Keep mismatch validation in sync: changing either field should revalidate both.
    useEffect(() => {
        if (isSecurityLocked) return;

        const hasSecurity = (form1.securityId || "").length > 0;
        const hasConfirm = (form1.validateSecurity || "").length > 0;
        const touched = securityTouchedRef.current || confirmSecurityTouchedRef.current;

        if (hasSecurity || hasConfirm || touched) {
            runFieldValidation("securityId", form1.securityId || "");
            runFieldValidation("validateSecurity", form1.validateSecurity || "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form1.securityId, form1.validateSecurity, isUS, isSecurityLocked]);


    // Clear SSN validation errors when Security ID is in read-only (prefilled) mode
    useEffect(() => {
        if (!isSecurityLocked) return;
        setFieldErrors((prev) => ({ ...prev, securityId: "", validateSecurity: "" }));
    }, [isSecurityLocked]);

    useEffect(() => {
        if (!isSecurityEditContext) return;
        if (lookupSecExistFlag !== "no") return;
        if (securityTouchedRef.current || confirmSecurityTouchedRef.current) return;

        const hasSeededSecurity = Boolean(
            extractDigits(form1.securityId, isUS) || extractDigits(form1.validateSecurity, isUS)
        );
        if (!hasSeededSecurity) return;

        setShowSecurityId(false);
        setShowConfirmSecurityId(false);
        setFieldErrors((prev) => ({ ...prev, securityId: "", validateSecurity: "" }));
        setForm1((prev) => ({
            ...prev,
            securityId: "",
            validateSecurity: ""
        }));
    }, [isSecurityEditContext, lookupSecExistFlag, form1.securityId, form1.validateSecurity, isUS]);

    const handleEnableSecurityEdit = () => {
        setSecurityEditEnabled(true);
        setFieldErrors((prev) => ({ ...prev, securityId: "", validateSecurity: "" }));

        const nextSecurityId = clampDigitsToFormat(isUS, extractDigits(form1.securityId, isUS));
        const nextValidateSecurity = clampDigitsToFormat(isUS, extractDigits(form1.validateSecurity, isUS));

        // If only last-4 (or fewer) digits are present, clear fields so user enters full value.
        // Otherwise preserve existing digits; if rule changed, clamp 9->8 instead of wiping.
        if (nextSecurityId && nextSecurityId.length <= 4) {
            setForm1((p) => ({ ...p, securityId: "", validateSecurity: "" }));
            setShowSecurityId(false);
            setShowConfirmSecurityId(false);
        } else {
            if (nextSecurityId !== form1.securityId || nextValidateSecurity !== form1.validateSecurity) {
                setForm1((p) => ({ ...p, securityId: nextSecurityId, validateSecurity: nextValidateSecurity }));
            }
            setShowSecurityId(false);
            setShowConfirmSecurityId(false);
        }

        queueMicrotask(() => {
            try { securityRef.current?.focus(); } catch { }
        });
    };

    const handleDisableSecurityEdit = () => {
        setSecurityEditEnabled(false);
        setShowSecurityId(false);
        setShowConfirmSecurityId(false);
        setFieldErrors((prev) => ({ ...prev, securityId: "", validateSecurity: "" }));
        // Revert to the originally-loaded digits (may be empty) so we don't accidentally send changes.
        setForm1((p) => ({
            ...p,
            securityId: securityInitialDigits || "",
            validateSecurity: securityInitialDigits || ""
        }));
    };

    // NOTE: Do not clear Security ID on Attuid/Country changes.
    // We preserve digits and let the formatting/validation rule update.
    const securityDisplayValue = useMemo(() => {
        const d = clampDigitsToFormat(isUS, extractDigits(form1.securityId, isUS));
        if (isSecurityLocked) {
            const max = maxDigitsFor(isUS);
            if (!d) return "*".repeat(max);
            if (d.length <= 4) return "*".repeat(Math.max(0, max - d.length)) + d;
            return buildUnmaskedFormatted(isUS, d);
        }
        if (!d) return "";
        return showSecurityId ? buildUnmaskedFormatted(isUS, d) : buildMaskedDisplay(isUS, d);
    }, [isUS, form1.securityId, showSecurityId, isSecurityLocked]);

    const confirmDisplayValue = useMemo(() => {
        const d = clampDigitsToFormat(isUS, extractDigits(form1.validateSecurity, isUS));
        if (isSecurityLocked) {
            const max = maxDigitsFor(isUS);
            if (!d) return "*".repeat(max);
            if (d.length <= 4) return "*".repeat(Math.max(0, max - d.length)) + d;
            return buildUnmaskedFormatted(isUS, d);
        }
        if (!d) return "";
        return showConfirmSecurityId
            ? buildUnmaskedFormatted(isUS, d)
            : buildMaskedDisplay(isUS, d);
    }, [isUS, form1.validateSecurity, showConfirmSecurityId, isSecurityLocked]);

    /* =========================
    SSN/Security ID existence check (Hinge Search)
    - Trigger: securityId has >= HINGE_SSN_MIN_DIGITS digits
    - Debounce: 2 seconds after user stops typing
    - Skips when Security ID is locked (prefilled) or URL not configured
    ========================= */
    useEffect(() => {
        // Not configured => do nothing
        if (!TB_HINGE_SEARCH_URL) return;

        // Do not call when the field is locked/prefilled
        if (isSecurityLocked) {
            setSsnAssociation({ status: "idle", message: "" });
            return;
        }

        const primaryErr = String(fieldErrors?.securityId || "").trim();
        const confirmErr = String(fieldErrors?.validateSecurity || "").trim();
        const hasPrimaryError = Boolean(primaryErr);
        const hasAnySecurityError = hasPrimaryError || Boolean(confirmErr);

        const digits = clampDigitsToFormat(isUS, extractDigits(form1.securityId || "", isUS));
        const min = Number.isFinite(HINGE_SSN_MIN_DIGITS) ? HINGE_SSN_MIN_DIGITS : 8;

        // Clear message when below min digits
        if (!digits || digits.length < min) {
            setSsnAssociation({ status: "idle", message: "" });
            if (ssnDebounceRef.current) clearTimeout(ssnDebounceRef.current);
            ssnDebounceRef.current = null;
            return;
        }

        // If the primary SSN/Security ID field is invalid, do not show availability messaging
        // and do not call Hinge search until it is valid.
        if (hasPrimaryError) {
            if (ssnDebounceRef.current) clearTimeout(ssnDebounceRef.current);
            ssnDebounceRef.current = null;
            setSsnAssociation({ status: "idle", message: "" });
            return;
        }

        // Debounce: wait for user to finish typing
        if (ssnDebounceRef.current) clearTimeout(ssnDebounceRef.current);

        setSsnAssociation((prev) => (prev?.status === "found" ? prev : { status: "checking", message: "" }));

        ssnDebounceRef.current = setTimeout(async () => {
            const seq = ++ssnReqSeqRef.current;

            try {
                const roleName = String(sessionStorage.getItem("tb_role") || "").trim();
                const supplierName = String(sessionStorage.getItem("tb_supplierName") || sessionStorage.getItem("tb_vendorId") || "").trim();
                const searchDtvId = String(sessionStorage.getItem("tb_dtvid") || "").trim();

                const params = new URLSearchParams();
                params.set("q", digits);
                params.set("isActive", "true");
                /*TODO:pentest*/ params.set("searchMode", "validate"); // special param to indicate identity validation is needed for Hinge Search API

                if (roleName) params.set("searcherRoleName", roleName);
                if (supplierName) params.set("searcherSupplierName", supplierName);
                if (searchDtvId) params.set("searchDtvId", searchDtvId);

                const url = `${TB_HINGE_SEARCH_URL}?${params.toString()}`;
                const resp = await tbGetJson(url);

                // Ignore stale responses
                if (seq !== ssnReqSeqRef.current) return;

                const arr = normalizeHingePeople(resp?.response ?? resp);
                if (Array.isArray(arr) && arr.length > 0) {
                    const nm = hingePersonFullName(arr[0]);
                    const msg = nm
                        ? `${securityLabel} is already associated to ${nm}.`
                        : `${securityLabel} is already associated to an existing person.`;
                    setSsnAssociation({ status: "found", message: msg });
                } else {
                    if (!isUS) {
                        const mmddMsg = validateSecurityIdMmdd8(digits);
                        if (mmddMsg) {
                            setSsnAssociation({ status: "idle", message: "" });
                            return;
                        }
                    }

                    // If there is any validation error (including confirm mismatch), do not show
                    // the green "available" message.
                    if (hasAnySecurityError) {
                        setSsnAssociation({ status: "idle", message: "" });
                        return;
                    }

                    setSsnAssociation({
                        status: "none",
                        message: isUS ? "SSN is available to use." : "Security ID is available."
                    });

                }
            } catch (e) {
                if (seq !== ssnReqSeqRef.current) return;
                console.warn("[ONBOARDING] SSN hinge-search check failed:", e?.message || e);
                setSsnAssociation({ status: "error", message: "" });
            }
        }, 2000);

        return () => {
            if (ssnDebounceRef.current) clearTimeout(ssnDebounceRef.current);
            ssnDebounceRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form1.securityId, fieldErrors?.securityId, fieldErrors?.validateSecurity, isUS, isSecurityLocked, TB_HINGE_SEARCH_URL]);


    // If we are on the NEW onboarding route (no :id and no external npwId),
    // force-create mode by clearing any previous edit context + cached draft IDs.
    useEffect(() => {
        const isNewRoute = !id && !npwId;
        if (!isNewRoute) return;

        setEditData(null);
        setDraftNpwId(null);
        setDraftClientRequestId(null);
        try {
            sessionStorage.removeItem("tb_draft_npw_id");
            sessionStorage.removeItem("tb_draft_client_request_id");
        } catch { }

        setStep(1);
        setForm1(initialVal);
        setSupplierLocked(false);
        setFieldErrors({});

        setSelectedCountry(null);
        setSelectedState(null);
        setSelectedCity(null);
        setCitizen(null);
        SetSelectedTimeZone(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, npwId]);

    // If we are in DTVID-prefill mode (route/query/state provides a DTVID but we do NOT have a numeric npw_id),
    // ensure we never carry over cached draft IDs from prior sessions (those can break bigint parsing downstream).
    useEffect(() => {
        const isDtvPrefill = Boolean((effectiveDtvid && String(effectiveDtvid).trim()) && !effectiveNpwId);
        if (!isDtvPrefill) return;

        setEditData(null);
        setDraftNpwId(null);
        setDraftClientRequestId(null);
        try {
            sessionStorage.removeItem("tb_draft_npw_id");
            sessionStorage.removeItem("tb_draft_client_request_id");
        } catch { }
    }, [effectiveDtvid, effectiveNpwId]);

    const handleSecKeyDown = (which /* "securityId" | "validateSecurity" */) => (e) => {
        if (isSecurityLocked) { e.preventDefault(); return; }
        const key = e.key;

        // Allow paste (handled in onPaste). Do not preventDefault here;
        if ((e.ctrlKey || e.metaKey)) {
            if (["v", "a", "c", "x"].includes(String(key || "").toLowerCase())) return;
        }

        const curDigits = clampDigitsToFormat(isUS, extractDigits(form1[which], isUS));
        const max = maxDigitsFor(isUS);
        const pos = digitPosFor(isUS);

        if (key === "Tab") return;

        if (key === "Backspace") {
            e.preventDefault();
            if (which === "securityId") securityTouchedRef.current = true;
            if (which === "validateSecurity") confirmSecurityTouchedRef.current = true;
            const next = curDigits.slice(0, -1);
            setForm1((p) => ({ ...p, [which]: next }));
            runFieldValidation(which, next);

            queueMicrotask(() => {
                const el = which === "securityId" ? securityRef.current : confirmSecurityRef.current;
                if (!next.length) setCaretToSlotIndex(el, 0);
                else setCaretToNextSlot(el, isUS, next.length);
            });
            return;
        }

        // SSN (US): 9 digits only; Security ID (non-US): MMDDXXXX (first 4 digits, last 4 alphanumeric)
        const isValidKey = (key, pos, isUS) => {
            if (isUS) {
                // SSN: only digits, max 9
                return /^[0-9]$/.test(key) && pos < 9;
            } else {
                // Security ID: MMDDXXXX
                if (pos < 4) return /^[0-9]$/.test(key); // MMDD
                if (pos >= 4 && pos < 8) return /^[A-Za-z0-9]$/.test(key); // XXXX
                return false;
            }
        };


        if (isValidKey(key, curDigits.length, isUS)) {
            e.preventDefault();
            if (which === "securityId") securityTouchedRef.current = true;
            if (which === "validateSecurity") confirmSecurityTouchedRef.current = true;
            if (curDigits.length >= max) return;

            const next = curDigits + key;
            setForm1((p) => ({ ...p, [which]: next }));
            runFieldValidation(which, next);

            queueMicrotask(() => {
                const el = which === "securityId" ? securityRef.current : confirmSecurityRef.current;
                if (el) {
                    if (next.length >= max) {
                        setCaretToSlotIndex(el, templateFor(isUS).length);
                    } else {
                        const nextSlot = pos[next.length] ?? templateFor(isUS).length;
                        setCaretToSlotIndex(el, nextSlot);
                    }
                }
            });
            return;
        }

        if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(key)) {
            e.preventDefault();
            const el = which === "securityId" ? securityRef.current : confirmSecurityRef.current;
            queueMicrotask(() => {
                if (!curDigits.length) setCaretToSlotIndex(el, 0);
                else setCaretToNextSlot(el, isUS, curDigits.length);
            });
            return;
        }

        e.preventDefault();
    };

    const handleSecChange = (which /* "securityId" | "validateSecurity" */) => (e) => {
        if (isSecurityLocked) return;
        const raw = e?.target?.value ?? "";
        const digits = clampDigitsToFormat(isUS, extractDigits(raw, isUS));
        if (which === "securityId") securityTouchedRef.current = true;
        if (which === "validateSecurity") confirmSecurityTouchedRef.current = true;
        setForm1((p) => ({ ...p, [which]: digits }));
        runFieldValidation(which, digits);
    };

    const handleSecPaste = (which) => (e) => {
        if (isSecurityLocked) { e.preventDefault(); return; }
        e.preventDefault();
        const clip = e.clipboardData?.getData("text") || "";
        const digits = clampDigitsToFormat(isUS, extractDigits(clip, isUS));
        if (!digits) return;

        if (which === "securityId") securityTouchedRef.current = true;
        if (which === "validateSecurity") confirmSecurityTouchedRef.current = true;

        setForm1((p) => ({ ...p, [which]: digits }));
        runFieldValidation(which, digits);

        queueMicrotask(() => {
            const el = which === "securityId" ? securityRef.current : confirmSecurityRef.current;
            setCaretToSlotIndex(el, templateFor(isUS).length);
        });
    };

    const handleSecFocus = (which) => () => {
        if (isSecurityLocked) return;
        const digits = clampDigitsToFormat(isUS, extractDigits(form1[which], isUS));
        const el = which === "securityId" ? securityRef.current : confirmSecurityRef.current;

        if (!digits) {
            queueMicrotask(() => setCaretToSlotIndex(el, 0));
            return;
        }
        queueMicrotask(() => setCaretToNextSlot(el, isUS, digits.length));
    };


    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minStartDate = new Date(today);
    minStartDate.setMonth(minStartDate.getMonth() - 1);
    const maxStartDate = new Date(today);
    maxStartDate.setMonth(maxStartDate.getMonth() + 1);
    const minEndDate = (() => {
        if (form1.startDate) {
            const start = new Date(
                form1.startDate.getFullYear(),
                form1.startDate.getMonth(),
                form1.startDate.getDate()
            );
            return start > today ? start : today;
        }
        return today;
    })();

    const attRoleOptions = (() => {
        const map = new Map();
        hingeRoleCatalog.forEach((role) => {
            const dtvRoleId = role.dtvRoleId;
            const dtvRoleName = role.dtvRoleName;
            (role.cinglepointRoles || []).forEach((cp) => {
                const cpRoleId = cp.cpRoleId;
                const cpRoleDesc = cp.cpRoleDesc;
                (cp.cpProfiles || []).forEach((profile) => {
                    const cpProfileId = profile.cpProfileId;
                    const cpProfileDesc = profile.cpProfileDesc;
                    const key = `${cpRoleId}||${cpProfileId}`;
                    if (!map.has(key)) {
                        map.set(key, {
                            key,
                            dtvRoleId,
                            dtvRoleName,
                            cpRoleId,
                            cpRoleDesc,
                            cpProfileId,
                            cpProfileDesc
                        });
                    }
                });
            });
        });
        return Array.from(map.values());
    })();

    const attRoleSelectOptions = attRoleOptions.map((o) => ({
        value: o.key,
        label: `${o.cpRoleId} - ${o.cpProfileDesc || ""}`,
        meta: o
    }));
    const selectedAttRoleOption =
        attRoleSelectOptions.find((o) => o.value === form1.attRoleKey) || null;


    // ===== Auto-select ATT Role on mount/changes (when Attuid Required is ON) =====
    useEffect(() => {
        if (!attuidRequiredOn) return;
        if (!form1.role) return;
        if (!Array.isArray(attRoleOptions) || attRoleOptions.length === 0) return;

        // If current selection already matches the chosen DTV role, skip
        if (form1.attRoleKey) {
            const current = attRoleOptions.find(o => `${o.cpRoleId}||${o.cpProfileId}` === form1.attRoleKey);
            if (current) {
                const r1 = String(current?.dtvRoleName || "").toLowerCase();
                const r2 = String(form1.role || "").toLowerCase();
                if (r1 === r2) return;
            }
        }

        const auto = autoSelectAttRoleForDtvRole(form1.role);
        if (auto) {
            setForm1(prev => ({ ...prev, ...auto }));
            setFieldErrors(prev => ({ ...prev, attRoleKey: "" }));
        }
    }, [attuidRequiredOn, form1.role, attRoleOptions.length]);



    // ===== Helper: auto-select ATT role mapped to the chosen DTV role =====
    const autoSelectAttRoleForDtvRole = (roleNameOrId) => {
        if (!roleNameOrId) return null;
        try {
            const needle = String(roleNameOrId).trim().toLowerCase();
            // 1) Build a lookup of dtvRoleName(lower) -> dtvRoleId from the catalog
            let dtvIdFromName = null;
            for (const r of hingeRoleCatalog || []) {
                const nm = (r?.dtvRoleName || "").toLowerCase();
                if (nm && nm === needle) {
                    dtvIdFromName = r?.dtvRoleId || null;
                    break;
                }
            }
            // If the caller already passed an ID, accept that
            const maybeId = /^\d+$/.test(needle) ? needle : null;
            const targetId = maybeId || (dtvIdFromName ? String(dtvIdFromName) : null);

            // 2) Find a matching att role by dtvRoleId first, fallback to name match
            let match = null;
            if (targetId) {
                match = attRoleOptions.find(o => String(o?.dtvRoleId || "").toLowerCase() == String(targetId).toLowerCase());
            }
            if (!match) {
                match = attRoleOptions.find(o =>
                    (o?.dtvRoleName || "").toLowerCase() === needle
                );
            }
            if (!match) return null;
            return {
                attRoleKey: `${match.cpRoleId}||${match.cpProfileId}`,
                attRoleDtvRoleId: match.dtvRoleId || "",
                attRoleDtvRoleName: match.dtvRoleName || "",
                attRoleCpRoleId: match.cpRoleId || "",
                attRoleCpRoleDesc: match.cpRoleDesc || "",
                attRoleCpProfileId: match.cpProfileId || "",
                attRoleCpProfileDesc: match.cpProfileDesc || ""
            };
        } catch {
            return null;
        }
    };

    const vendorIdFromSession = sessionStorage?.getItem("tb_vendorId") || "";
    const supplierNameFromSession = sessionStorage?.getItem("tb_supplierName") || "";

    useEffect(() => {
        const vendor = (vendorIdFromSession || supplierNameFromSession || "").trim();
        if (vendor) setLoginVendorName(vendor);
    }, [vendorIdFromSession, supplierNameFromSession]);

    useEffect(() => {
        if (!loginVendorName || editData) return;
        setForm1((prev) => ({
            ...prev,
            vendor: prev.vendor && prev.vendor !== "Vendor Name" ? prev.vendor : loginVendorName
        }));
    }, [loginVendorName, editData]);

    useEffect(() => {
        if (!npwId || !TB_SEARCH_URL) return;
        (async () => {
            try {
                setLoading(true);
                const url = `${TB_SEARCH_URL}?npwId=${encodeURIComponent(
                    npwId
                )}&action=editLoadNewOnboard`;
                const rspns = await callApiOnce(`editLoadNewOnboard:${npwId}`, () => tbGetJson(url));
                const parsed = typeof rspns === "string" ? JSON.parse(rspns) : rspns;
                if (parsed?.data?.[0]) {
                    const mapped = mapApiToForm1(parsed.data[0]);
                    setLookupSecExistFlag(normalizeLookupSecurityExistence(mapped?.isSecExist));
                    setEditData({ ...mapped, npwId });
                    setForm1(mapped);
                } else {
                    navigate(location.pathname, {
                        state: { toastMessage: "Item not found" },
                        replace: true
                    });
                }
            } catch (e) {
                console.error("editLoadNewOnboard failed", e);
                toast.error("Failed to load item for edit.");
                navigate(location.pathname, {
                    state: { toastMessage: "Item not found" },
                    replace: true
                });
            } finally {
                setLoading(false);
            }
        })();
    }, [npwId, navigate, location.pathname]);

    const editLoadSearchableDDval = (val, optn, setFn) => {
        const vRaw = String(val || "").trim();
        if (!vRaw) return;
        const v = vRaw.toLowerCase();
        const list = optn || [];

        const match =
            list.find((c) => String(c?.label || "").trim().toLowerCase() === v) ||
            list.find((c) => String(c?.value || "").trim().toLowerCase() === v) ||
            list.find((c) => String(c?.label || "").trim().toLowerCase().includes(v)) ||
            list.find((c) => v.includes(String(c?.label || "").trim().toLowerCase()));

        setFn(match || null);
    };

    const editHydratedKeyRef = useRef(null);
    const geoCountryHydratedKeyRef = useRef(null);
    const geoStateHydratedKeyRef = useRef(null);
    const geoCityHydratedKeyRef = useRef(null);

    useEffect(() => {
        if (!editData) return;

        // IMPORTANT: Only hydrate form state once per edit record.
        // Otherwise, changing dropdowns (Country/Attuid Required) can "snap back" to editData values.
        const key = String(editData?.npw_id || editData?.npwId || editData?.client_request_id || "__edit__");
        if (editHydratedKeyRef.current === key) return;
        editHydratedKeyRef.current = key;

        const ed = { ...editData, validateSecurity: editData.securityId };
        // Worker Type is fixed to NPW in TeamBridge
        setForm1(ed);
        if (ed.supplierReference) setSupplierLocked(true);
        setLoading(false);
    }, [editData]);

    // After hydration, sync react-select values once options exist.
    // Do not override user changes (only set when corresponding select is still unset).
    useEffect(() => {
        if (!editData) return;

        const key = String(editData?.npw_id || editData?.npwId || editData?.client_request_id || "__edit__");

        if (!selectedCountry && editData.country && geoCountryHydratedKeyRef.current !== key) {
            const cOpt = (countryOptions || []).find((c) => c.label === editData.country || c.value === editData.country);
            if (cOpt) {
                setSelectedCountry(cOpt);
                geoCountryHydratedKeyRef.current = key;
            }
        }

        if (selectedCountry && !selectedState && editData.state && geoStateHydratedKeyRef.current !== key) {
            editLoadSearchableDDval(editData.state, stateOptions, (opt) => {
                if (!opt) return;
                setSelectedState(opt);
                geoStateHydratedKeyRef.current = key;
            });
        }

        if (!ctzn && form1.citizenship) {
            editLoadSearchableDDval(
                form1.citizenship,
                (citizenshipOptions.length ? citizenshipOptions : countryOptions),
                setCitizen
            );
        }

        if (!selectedTimeZone && form1.timeZone) {
            editLoadSearchableDDval(form1.timeZone, timeZoneOptions, SetSelectedTimeZone);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editData, form1.citizenship, form1.timeZone, countryOptions, citizenshipOptions, stateOptions, selectedCountry, selectedState, ctzn, selectedTimeZone]);

    useEffect(() => {
        if (!editData) return;
        const key = String(editData?.npw_id || editData?.npwId || editData?.client_request_id || "__edit__");
        if (!selectedCountry || !selectedState || !cityOptions.length) return;
        if (selectedCity) return;
        if (editData.city && geoCityHydratedKeyRef.current !== key) {
            const normCity = (vv) =>
                String(vv || "")
                    .trim()
                    .toLowerCase()
                    .replace(/\(.*?\)/g, "")
                    .replace(/,/g, " ")
                    .replace(/^city and county of\s+/i, "")
                    .replace(/^county of\s+/i, "")
                    .replace(/^city of\s+/i, "")
                    .replace(/\s+/g, " ")
                    .trim();

            const target = normCity(editData.city);
            const match =
                (cityOptions || []).find((c) => normCity(c?.label) === target || normCity(c?.value) === target) ||
                (cityOptions || []).find((c) => normCity(c?.label).includes(target) || normCity(c?.value).includes(target)) ||
                (cityOptions || []).find((c) => target.includes(normCity(c?.label)) || target.includes(normCity(c?.value)));

            if (match) {
                setSelectedCity(match);
                geoCityHydratedKeyRef.current = key;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editData, selectedCountry, selectedState, cityOptions, selectedCity]);

    useEffect(() => {
        // Role catalog is only needed when ATTUID Required is ON.
        if (!attuidRequiredOn) {
            // Clear any previously loaded catalog to avoid stale auto-selection.
            setHingeRoleCatalog([]);
            setDd((prev) => ({ ...prev, roles: STATIC_DROPDOWNS.roles }));
            // Ensure role data-load state does not trigger load checks/toasts when ATTUID is off.
            setDataLoads((prev) => ({
                ...prev,
                roles: { field: "Role", status: "idle", startedAt: null }
            }));
            return;
        }
        if (!ENABLE_HINGE_ROLE_DROPDOWN || !TB_HINGE_ROLE_LIST_URL) return;
        (async () => {
            try {
                setDataLoads((prev) => ({
                    ...prev,
                    roles: {
                        field: "Role",
                        status: "loading",
                        startedAt:
                            prev?.roles?.status === "loading" && prev?.roles?.startedAt
                                ? prev.roles.startedAt
                                : Date.now()
                    }
                }));
                const roleResp = await callApiOnce(`hingeRoleCatalog:${TB_HINGE_ROLE_LIST_URL}`, () => tbGetJson(TB_HINGE_ROLE_LIST_URL));

                // Support multiple shapes:
                // - Hinge direct: { roles: [...] }
                // - TB wrapper:   { requestId, timing, response: { roles: [...] } }
                // - Array:        [ ... ]
                const payload = roleResp?.response && typeof roleResp.response === "object" ? roleResp.response : roleResp;

                let catalog = [];
                let dtvRoleNames = [];
                if (Array.isArray(payload?.roles)) {
                    catalog = payload.roles;
                    dtvRoleNames = [
                        ...new Set(
                            payload.roles
                                .map((r) => r?.dtvRoleName || r?.dtv_role_name || r?.roleName || r?.role_name || r?.name)
                                .filter(Boolean)
                        )
                    ];
                } else if (Array.isArray(payload)) {
                    catalog = payload;
                    dtvRoleNames = [
                        ...new Set(
                            payload
                                .map((r) => r?.dtvRoleName || r?.dtv_role_name || r?.roleName || r?.role_name || r?.name)
                                .filter(Boolean)
                        )
                    ];
                }
                if (catalog.length) setHingeRoleCatalog(catalog);
                if (dtvRoleNames.length) {
                    setDd((prev) => ({
                        ...prev,
                        roles: dtvRoleNames
                    }));
                }

                setDataLoads((prev) => ({
                    ...prev,
                    roles: { ...prev.roles, field: "Role", status: "success" }
                }));
            } catch (err) {
                console.warn("Hinge role dropdown fetch failed", err);
                setDataLoads((prev) => ({
                    ...prev,
                    roles: { ...prev.roles, field: "Role", status: "error" }
                }));
            }
        })();
    }, [attuidRequiredOn]);

    useEffect(() => {
        // Vendor/Supplier load is irrelevant once edit mode is active or supplier is locked.
        if (editData || supplierLocked) {
            setDataLoads((prev) => ({
                ...prev,
                vendorSupplier: { ...prev.vendorSupplier, field: "Vendor/Supplier", status: "success" }
            }));
            return;
        }
        if (!loginVendorName) {
            setDataLoads((prev) => ({
                ...prev,
                vendorSupplier: { ...prev.vendorSupplier, field: "Vendor/Supplier", status: "idle" }
            }));
        }
    }, [editData, supplierLocked, loginVendorName]);

    useEffect(() => {
        // If route param represents a DTVID (= prefill) do not call approval-details.
        // Load draft/details only when we have an effective NPW id (prop npwId OR route id).
        if (!effectiveNpwId) return;
        if (effectiveDtvid) return;

        (async () => {
            try {
                setLoading(true);
                const resp = await callApiOnce(`approvalDetails:${effectiveNpwId}`, () => apiFetchOnboardingById(effectiveNpwId));
                const draft =
                    resp?.data && typeof resp.data === "object" ? resp.data : resp;
                const mapped = mapApiToForm1(draft || {});
                setLookupSecExistFlag(normalizeLookupSecurityExistence(mapped?.isSecExist));
                setForm1(mapped);
                setEditData(mapped);

                // Track draft ids (npw_id + client_request_id) so later Submit uses EDIT
                const respNpw = draft?.npw_id || draft?.npwId || effectiveNpwId || null;
                const respClient = draft?.client_request_id || draft?.clientRequestId || null;
                if (respNpw) {
                    const v = String(respNpw).trim();
                    if (/^\d+$/.test(v)) {
                        setDraftNpwId(v);
                        sessionStorage.setItem("tb_draft_npw_id", v);
                    } else {
                        // Never persist non-numeric IDs (ex: "DTVID=...") as draft NPW IDs.
                        setDraftNpwId(null);
                        try { sessionStorage.removeItem("tb_draft_npw_id"); } catch { }
                    }
                }
                if (respClient) {
                    const v = String(respClient);
                    setDraftClientRequestId(v);
                    sessionStorage.setItem("tb_draft_client_request_id", v);
                }

                // hydrate geo pickers
                if (mapped.country) {
                    const allCountries = Country.getAllCountries();
                    const cMatch = allCountries.find((c) => c.name === mapped.country);
                    if (cMatch) {
                        const cOpt = { label: cMatch.name, value: cMatch.isoCode };
                        setSelectedCountry(cOpt);
                        const allStates = State.getStatesOfCountry(cMatch.isoCode);
                        if (mapped.state) {
                            const sMatch = allStates.find((s) => s.name === mapped.state);
                            if (sMatch) {
                                const sOpt = { label: sMatch.name, value: sMatch.isoCode };
                                setSelectedState(sOpt);
                                if (mapped.city) {
                                    const allCities = City.getCitiesOfState(
                                        cMatch.isoCode,
                                        sMatch.isoCode
                                    );
                                    const cityMatch = allCities.find((x) => x.name === mapped.city);
                                    if (cityMatch) {
                                        setSelectedCity({ label: cityMatch.name, value: cityMatch.name });
                                    }
                                }
                            }
                        }
                    }
                }

                if (mapped.citizenship) {
                    setCitizen({ label: mapped.citizenship, value: mapped.citizenship });
                }
                if (mapped.timeZone) {
                    const tzOpt = timeZoneOptions.find(
                        (z) => z.label.includes(mapped.timeZone) || z.value === mapped.timeZone
                    );
                    if (tzOpt) SetSelectedTimeZone(tzOpt);
                }
                if (mapped.supplierReference) setSupplierLocked(true);

                // existing behavior: show review step on load
                setStep(2);
                toast.info("Loaded draft into review.", { toastId: "load-draft" });
            } catch (e) {
                console.error(e);
                toast.error("Failed to load draft.");
            } finally {
                setLoading(false);
            }
        })();
    }, [effectiveNpwId, effectiveDtvid]);
    useEffect(() => {
        if (!CP_COUNTRYLIST_URL) return;
        (async () => {
            try {
                const data = await callApiOnce(`cp-countrylist:${CP_COUNTRYLIST_URL}`, () => getJson(CP_COUNTRYLIST_URL));
                const list = Array.isArray(data?.citizenships) ? data.citizenships : Array.isArray(data) ? data : [];
                const opts = list
                    .map((s) => String(s || "").trim())
                    .filter(Boolean)
                    .sort((a, b) => a.localeCompare(b))
                    .map((s) => ({ label: s, value: s }));
                if (opts.length) {
                    setCitizenshipOptions(opts);

                    // If we already have a string value (prefill/edit), sync react-select value once options exist.
                    setCitizen((prev) => {
                        const currentLabel = prev?.label || form1?.citizenship || "";
                        if (!currentLabel) return prev;
                        const match = opts.find((o) => o.label === currentLabel);
                        return match || prev;
                    });
                }
            } catch (e) {
                console.warn("cp-countrylist load failed; falling back to country-state-city list", e);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [CP_COUNTRYLIST_URL]);



    useEffect(() => {
        const countries = Country.getAllCountries().map((c) => ({
            label: c.name,
            value: c.isoCode
        }));
        setCountryOptions(countries);
    }, []);

    useEffect(() => {
        if (selectedCountry) {
            const states = State.getStatesOfCountry(selectedCountry.value).map((s) => ({
                label: s.name,
                value: s.isoCode
            }));
            setStateOptions(states);
        } else {
            setStateOptions([]);
        }

        // Programmatic prefill: keep any state/city we just set.
        if (suppressCountryCascadeRef.current) {
            suppressCountryCascadeRef.current = false;
            return;
        }

        if (!editData && !isEditRoute) {
            setSelectedState(null);
            setCityOptions([]);
            setSelectedCity(null);
            setForm1((p) => ({
                ...p,
                country: selectedCountry?.label || "",
                state: "",
                city: ""
            }));
            runFieldValidation("country", selectedCountry?.label || "");
        } else {
            setForm1((p) => ({
                ...p,
                country: selectedCountry?.label || p.country
            }));
        }
    }, [selectedCountry, editData]);

    useEffect(() => {
        const phoneVal = String(form1.phone || "").trim();
        if (!phoneVal) {
            setFieldErrors((prev) => ({ ...prev, phone: "" }));
            return;
        }
        runFieldValidation("phone", phoneVal);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form1.country]);

    useEffect(() => {
        if (selectedCountry && selectedState) {
            const cities = City.getCitiesOfState(
                selectedCountry.value,
                selectedState.value
            ).map((city) => ({
                label: city.name,
                value: city.name
            }));
            setCityOptions(cities);
        } else {
            setCityOptions([]);
        }

        // Programmatic prefill: keep any city we just set.
        if (suppressStateCascadeRef.current) {
            suppressStateCascadeRef.current = false;
            return;
        }

        if (!editData && !isEditRoute) {
            setSelectedCity(null);
            setForm1((p) => ({
                ...p,
                state: selectedState?.label || "",
                city: ""
            }));
            runFieldValidation("state", selectedState?.label || "");
        }
    }, [selectedState, selectedCountry, editData]);

    const handleCityChange = (opt) => {
        setSelectedCity(opt);
        setForm1((prev) => ({
            ...prev,
            city: opt?.label || "",
            state: selectedState?.label || "",
            country: selectedCountry?.label || ""
        }));
        runFieldValidation("city", opt?.label || "");
    };

    useEffect(() => {
        if (!loginVendorName || !SUPPLIER_VALIDATION_URL) return;
        if (editData || supplierLocked) return;
        (async () => {
            try {
                setDataLoads((prev) => ({
                    ...prev,
                    vendorSupplier: {
                        field: "Vendor/Supplier",
                        status: "loading",
                        startedAt:
                            prev?.vendorSupplier?.status === "loading" && prev?.vendorSupplier?.startedAt
                                ? prev.vendorSupplier.startedAt
                                : Date.now()
                    }
                }));
                const base = SUPPLIER_VALIDATION_URL;
                const sep = base.includes("?") ? "&" : "?";
                const url = `${base}${sep}SupplierName=${encodeURIComponent(
                    loginVendorName
                )}`;
                const resp = await callApiOnce(`supplierValidation:${loginVendorName}`, () => getJson(url));
                const items = resp?.items || [];

                if (resp?.ok && items.length > 0) {
                    const item = items[0];
                    setForm1((prev) => ({
                        ...prev,
                        supplierReference:
                            item.supplierNumber || prev.supplierReference,
                        vendor: item.supplierName || loginVendorName
                    }));
                    setSupplierLocked(true);
                    setDataLoads((prev) => ({
                        ...prev,
                        vendorSupplier: { ...prev.vendorSupplier, field: "Vendor/Supplier", status: "success" }
                    }));
                } else {
                    // Treat "ok but no supplier items" as a failure (per requirement).
                    setDataLoads((prev) => ({
                        ...prev,
                        vendorSupplier: { ...prev.vendorSupplier, field: "Vendor/Supplier", status: "error" }
                    }));
                    toast.error(SUPPLIER_VALIDATION_BLOCK_MSG, { toastId: SUPPLIER_VALIDATION_BLOCK_TOAST_ID });
                }
            } catch (err) {
                console.warn("Supplier validation lookup failed", err);
                setDataLoads((prev) => ({
                    ...prev,
                    vendorSupplier: { ...prev.vendorSupplier, field: "Vendor/Supplier", status: "error" }
                }));
            }
        })();
    }, [loginVendorName, editData, supplierLocked]);

    // If Supplier/S2P lookup fails, hard-block the user until reload (per requirement).
    useEffect(() => {
        const st = dataLoads?.vendorSupplier?.status;
        if (st === "error") {
            toast.error(SUPPLIER_VALIDATION_BLOCK_MSG, { toastId: SUPPLIER_VALIDATION_BLOCK_TOAST_ID });
        }
    }, [dataLoads?.vendorSupplier?.status]);


    useEffect(() => {
        if (!ENABLE_DTV_PREFILL || !HINGE_DTV_LOOKUP_URL) return;
        // Skip prefill for true edit/draft flows. Allow DTVID route param to prefill.
        if (editData || effectiveNpwId) return;

        const parseMdy = (s) => {
            const v = String(s || "").trim();
            if (!v) return null;
            // Accept MM/DD/YYYY (Hinge) or YYYY-MM-DD
            const m1 = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
            if (m1) {
                const mm = Number(m1[1]);
                const dd = Number(m1[2]);
                const yy = Number(m1[3]);
                const d = new Date(yy, mm - 1, dd);
                return isNaN(+d) ? null : d;
            }
            const d2 = new Date(v);
            return isNaN(+d2) ? null : d2;
        };

        const pickFirst = (...vals) => {
            for (const x of vals) {
                if (x === 0) return 0;
                if (x === false) return false;
                const s = String(x ?? "").trim();
                if (s) return x;
            }
            return "";
        };

        const toYesNo = (v) => {
            if (v === true) return "Yes";
            if (v === false) return "No";
            const s = String(v || "").trim().toLowerCase();
            if (["y", "yes", "true", "1"].includes(s)) return "Yes";
            if (["n", "no", "false", "0"].includes(s)) return "No";
            return "";
        };

        const normWorkerTypeTax = (v) => {
            const s = String(v || "").trim().toLowerCase();
            if (!s) return "";
            if (s === "w2" || s === "w-2" || s === "w 2" || s === "w_2") return "W-2";
            if (s === "1099" || s === "w2/1099" || s === "w2 / 1099") return "1099";
            if (s.includes("international")) return "International";
            // keep original if it matches dropdowns
            if (["w-2", "1099", "international"].includes(s)) return s === "w-2" ? "W-2" : s === "1099" ? "1099" : "International";
            return v;
        };

        // Use the same dtvId resolution logic as the top-of-page parsing.
        // (route "DTVID=..." token, query-string, location.state, or bare route token)
        const dtv = (effectiveDtvid || "").trim();
        if (!dtv) return;

        (async () => {
            try {
                setLoading(true);
                const base = HINGE_DTV_LOOKUP_URL;
                const sep = base.includes("?") ? "&" : "?";
                const url = `${base}${sep}dtvId=${encodeURIComponent(dtv)}`;
                const resp = await callApiOnce(`dtvPrefill:${dtv}`, () => tbGetJson(url));
                const lookupSecFlag = getLookupSecurityExistenceFromPayload(resp);
                setLookupSecExistFlag(lookupSecFlag);

                // Support multiple response shapes (tb-hinge proxy / direct hinge)
                // TB-Hinge lookup commonly returns:
                //   { requestId, timing, response: { dtvAnchorId, firstName, ... } }
                // whereas direct Hinge may return arrays like { personList: [...] }.
                const unwrapLookupPayload = (r) => {
                    let p = r;
                    for (let i = 0; i < 3; i++) {
                        if (p && typeof p === "object" && Object.prototype.hasOwnProperty.call(p, "data") && p.data && typeof p.data === "object") {
                            p = p.data;
                            continue;
                        }
                        if (p && typeof p === "object" && Object.prototype.hasOwnProperty.call(p, "response")) {
                            const rr = p.response;
                            if (typeof rr === "string") {
                                try {
                                    p = JSON.parse(rr);
                                    continue;
                                } catch {
                                    // ignore
                                }
                            }
                            if (rr && typeof rr === "object") {
                                p = rr;
                                continue;
                            }
                        }
                        break;
                    }
                    return p;
                };

                const payload = unwrapLookupPayload(resp);

                const person =
                    payload?.personList?.[0] ||
                    payload?.person?.[0] ||
                    payload?.person ||
                    payload?.data?.[0] ||
                    payload?.items?.[0] ||
                    // If payload itself is the person object (fields like firstName/dtvAnchorId), use it.
                    (payload && (payload.firstName || payload.lastName || payload.dtvAnchorId || payload.primaryEmail || payload.attuid || payload.attUid || payload.attUID) ? payload : null) ||
                    (Array.isArray(payload) ? payload[0] : null);

                if (person) {
                    // NOTE: For edit-like Security ID/SSN behavior, we do NOT gate on ATTUID.

                    const firstName = pickFirst(person.firstName, person.first_name);
                    const lastName = pickFirst(person.lastName, person.last_name);
                    const emailId = pickFirst(person.secondaryEmail, person.primaryEmail, person.email, person.primary_email);
                    const phone = pickFirst(person.phone, person.phoneNumber, person.mobile);
                    let citizenship = pickFirst(person.countryOfCitizenship, person.citizenship);
                    let countryWorking = pickFirst(person.countryOfWorking, person.country, person.countryOfWork, person.country_of_working);
                    const stateWorking = pickFirst(person.state, person.stateCode, person.state_code);
                    const cityWorking = pickFirst(person.city, person.cityName, person.city_name);
                    const timeZone = pickFirst(person.timeZone, person.timezone, person.time_zone);
                    const startDate = parseMdy(pickFirst(person.effectiveStartDate, person.startDate, person.start_date));
                    const endDate = parseMdy(pickFirst(person.effectiveEndDate, person.endDate, person.end_date));

                    /*Patch: handled country data issue for USA */
                    if ((citizenship === 'USA' && !countryWorking) || countryWorking === 'USA') {
                        countryWorking = 'United States';
                    }
                    if (citizenship === 'USA') {
                        citizenship = 'United States of America (USA) Citizen';
                    }

                    // Active/Terminated DTV lookup should behave like an edit-only Security ID UI.
                    const statusRaw = pickFirst(person.personStatus, person.status, person.employeeStatus, person.person_status);
                    const statusLc = String(statusRaw || "").trim().toLowerCase();
                    const isExistingRecord = /(^|\b)(active|inactive|terminated)(\b|$)/.test(statusLc);
                    setDtvPrefillExistingRecord(isExistingRecord);
                    setDtvPrefillPersonStatus(statusRaw || "");
                    const shouldLockSecurityForExisting = isExistingRecord && lookupSecFlag === "yes";

                    // ATTUID Required (lookup-prefill rule):
                    // For Active/Inactive/Terminated lookup results, derive ATTUID Required from
                    // cinglepointRoleId / cinglepointProfileId (both empty => No, else Yes).
                    // For other statuses, fall back to legacy isAttuidRequested/attuidRequired fields.
                    const cleanLookupVal = (v) => {
                        const s = String(v ?? "").trim();
                        if (!s) return "";
                        const lc = s.toLowerCase();
                        if (lc === "null" || lc === "undefined") return "";
                        return s;
                    };

                    const spRoleId =
                        cleanLookupVal(person.cinglepointRoleId) ||
                        cleanLookupVal(person.singlepointRoleId) ||
                        cleanLookupVal(person.cinglePointRoleId) ||
                        cleanLookupVal(person.singlePointRoleId);
                    const spProfileId =
                        cleanLookupVal(person.cinglepointProfileId) ||
                        cleanLookupVal(person.singlepointProfileId) ||
                        cleanLookupVal(person.cinglePointProfileId) ||
                        cleanLookupVal(person.singlePointProfileId);

                    const attuidFromSinglePoint = Boolean(spRoleId || spProfileId);
                    const isAttuidRequested = isExistingRecord
                        ? attuidFromSinglePoint
                        : (person.isAttuidRequested || person.attuidRequired || false);
                    const attuidYesNo = isAttuidRequested ? "Yes" : "No";

                    const last4Digits = extractDigits(pickFirst(person.ssnLast4d, person.ssn_last4d, person.ssnLast4, person.ssn_last4));
                    if (shouldLockSecurityForExisting && !securityInitCapturedRef.current) {
                        securityInitCapturedRef.current = true;
                        setSecurityInitialDigits(last4Digits || "");
                        const attuidOn = isAttuidRequested === true || String(isAttuidRequested || "").toLowerCase() === "true" || String(isAttuidRequested || "").toLowerCase() === "yes";
                        securityInitialIsUSRef.current = attuidOn && isUnitedStatesSelected(countryWorking);
                    }

                    const sponsorId = String(
                        pickFirst(person.employeeSponsor, person.sponsor, person.manager, person.approverDtvId)
                    ).trim();
                    const mgrId = String(
                        pickFirst(person.supervisorDtvAnchorId, person.supervisorAttuid, person.webphoneManagerId, person.webphone_manager)
                    ).trim();

                    const vendorFromHinge = pickFirst(person.supplierName, person.vendor);
                    const supplierReference = pickFirst(person.supplierReference, person.supplier_reference);
                    const contractPo = pickFirst(person.contractNumber, person.contractPo, person.contract, person.contract_po);
                    const poNum = pickFirst(person.poNumber, person.poNum, person.po_number);
                    const lineNum = pickFirst(person.lineNumber, person.lineNum, person.line_number);

                    const nonBillable = toYesNo(pickFirst(person.nonBillableIndicator, person.non_billable));
                    const occupancy = String(pickFirst(person.occupancyIndicator, person.occupancy)).trim().toLowerCase();
                    const occupancyCode = occupancy === "virtual" || occupancy === "office" ? occupancy : "";

                    const roleName = pickFirst(person.dtvRoleName, person.role);

                    const workerTypeTax = normWorkerTypeTax(pickFirst(person.w2_1099, person.workerTypeTax, person.worker_type_tax));

                    // Worker/NPW type comes from lookup as `personType`.
                    const npwTypeFromHinge = String(pickFirst(person.personType, person.employment_type, person.npw_type) || "").trim();

                    // ATTUID from lookup (when present, send it in payload on create/edit)
                    const attuid = String(pickFirst(person.attuid, person.attUid, person.attUID) || "").trim();
                    setLookupAttuid(attuid);

                    // Prefer anchor id returned by lookup payload (if present)
                    const anchorId = pickFirst(person.dtvAnchorId, person.dtvId, person.dtv_id, dtv);

                    setForm1((prev) => ({
                        ...prev,
                        dtvId: anchorId || dtv,
                        attuid: prev.attuid || attuid || "",

                        firstName: prev.firstName || firstName || "",
                        lastName: prev.lastName || lastName || "",
                        email: prev.email || emailId || "",
                        phone: prev.phone || phone || "",

                        citizenship: prev.citizenship || citizenship || "",
                        country: prev.country || countryWorking || "",
                        state: prev.state || stateWorking || "",
                        city: prev.city || cityWorking || "",
                        timeZone: prev.timeZone || timeZone || "",
                        isAttuidRequested: attuidYesNo,
                        attuidRequired: attuidYesNo,

                        // Security ID: when only last-4 is known (Active/Terminated), show masked last-4 in locked mode.
                        securityId:
                            shouldLockSecurityForExisting && last4Digits && !extractDigits(prev.securityId, isUS)
                                ? last4Digits
                                : prev.securityId,
                        validateSecurity:
                            shouldLockSecurityForExisting && last4Digits && !extractDigits(prev.validateSecurity, isUS)
                                ? last4Digits
                                : prev.validateSecurity,


                        // Sponsor / Webphone manager
                        sponsor: prev.sponsor || (sponsorId ? sponsorId : ""),
                        webphoneManagerId:
                            prev.webphoneManagerId || (mgrId ? mgrId : ""),

                        // Assignment info
                        startDate: prev.startDate || startDate || null,
                        endDate: prev.endDate || endDate || null,

                        // Role (if hinge provides it)
                        role: prev.role || roleName || "",

                        // ATT-related fields (if hinge provides them)
                        workerTypeTax: prev.workerTypeTax || workerTypeTax || "",
                        nonBillable: prev.nonBillable || nonBillable || "",
                        occupancyCode: prev.occupancyCode || occupancyCode || "",

                        // NPW/Worker type from lookup (personType); override the default placeholder.
                        npwType:
                            prev.npwType && prev.npwType !== "Non-staff Supp Worker"
                                ? prev.npwType
                                : (npwTypeFromHinge || prev.npwType),
                        npwTypeRaw: prev.npwTypeRaw || npwTypeFromHinge || null,

                        // Vendor/SupplierName: only set if user hasn't already got a real vendor value
                        vendor:
                            prev.vendor && prev.vendor !== "Vendor Name"
                                ? prev.vendor
                                : vendorFromHinge || prev.vendor,
                        supplierReference: prev.supplierReference || supplierReference || "",
                        contractPo: prev.contractPo || contractPo || "",
                        poNum: prev.poNum || poNum || "",
                        lineNum: prev.lineNum || lineNum || "",
                    }));

                    if (shouldLockSecurityForExisting) {
                        setShowSecurityId(false);
                        setShowConfirmSecurityId(false);
                        setSecurityEditEnabled(false);
                    }

                    // Keep react-select values in sync with prefilled strings. /* Not working due to no editData in useEffect */
                    // if (citizenship) editLoadSearchableDDval(citizenship, countryOptions, setCitizen);
                    // if (countryWorking) editLoadSearchableDDval(countryWorking, countryOptions, setSelectedCountry);
                    // if (stateWorking) editLoadSearchableDDval(stateWorking, stateOptions, setSelectedState);
                    // if (cityWorking) editLoadSearchableDDval(cityWorking, cityOptions, setSelectedCity);
                    if (timeZone) editLoadSearchableDDval(timeZone, timeZoneOptions, SetSelectedTimeZone);

                    if (citizenship) {
                        setCitizen({ label: citizenship, value: citizenship });
                    }

                    if (countryWorking) {
                        const norm = (v) => String(v || "").trim().toLowerCase();
                        const normCity = (v) =>
                            norm(v)
                                .replace(/\(.*?\)/g, "")
                                .replace(/,/g, " ")
                                .replace(/^city and county of\s+/i, "")
                                .replace(/^county of\s+/i, "")
                                .replace(/^city of\s+/i, "")
                                .replace(/\s+/g, " ")
                                .trim();

                        const allCountries = Country.getAllCountries();
                        const cMatch = allCountries.find(
                            (c) => c.name === countryWorking
                        );
                        if (cMatch) {
                            const cOpt = {
                                label: cMatch.name,
                                value: cMatch.isoCode
                            };
                            suppressCountryCascadeRef.current = true;
                            setSelectedCountry(cOpt);


                            const allStates = State.getStatesOfCountry(
                                cMatch.isoCode
                            );
                            if (stateWorking) {
                                const targetState = norm(stateWorking);
                                const sMatch = allStates.find(
                                    (s) => norm(s.name) === targetState || norm(s.isoCode) === targetState
                                );
                                if (sMatch) {
                                    const sOpt = {
                                        label: sMatch.name,
                                        value: sMatch.isoCode
                                    };
                                    suppressStateCascadeRef.current = true;
                                    setSelectedState(sOpt);

                                    if (cityWorking) {
                                        const allCities = City.getCitiesOfState(
                                            cMatch.isoCode,
                                            sMatch.isoCode
                                        ).map((city) => ({
                                            label: city.name,
                                            value: city.name
                                        }));

                                        setCityOptions(allCities); //if no editdata it may not have cities loaded yet

                                        const targetCity = normCity(cityWorking);
                                        const cityMatch =
                                            allCities.find((x) => normCity(x.value) === targetCity) ||
                                            allCities.find((x) => normCity(x.value).includes(targetCity)) ||
                                            allCities.find((x) => targetCity.includes(normCity(x.value)));
                                        if (cityMatch) {
                                            setSelectedCity({
                                                label: cityMatch.value,
                                                value: cityMatch.value
                                            });
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            } catch (err) {
                console.warn("Hinge DTV prefill failed", err);
            } finally {
                setLoading(false);
            }
        })();
    }, [location, editData, id, effectiveNpwId, effectiveDtvid]);
    useEffect(() => {
        if (!ENABLE_SPONSOR_VALIDATION || !TB_HINGE_SPONSOR_VALIDATE_URL) return;
        const sponsorVal = String(form1.sponsor || "").trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const hasFrontendSponsorError = !sponsorVal || emailRegex.test(sponsorVal) || sponsorVal.length > DTVUID_MAX_LEN;

        if (hasFrontendSponsorError) {
            setSponsorValidation({ status: "idle", message: "" });
            // keep emails in sync with the validated IDs
            setForm1((prev) => ({ ...prev, sponsorEmail: "", sponsor_email: "" }));
            return;
        }
        let cancelled = false;
        setSponsorValidation({ status: "checking", message: "" });
        const handle = setTimeout(async () => {
            try {
                const base = TB_HINGE_SPONSOR_VALIDATE_URL;
                const sep = base.includes("?") ? "&" : "?";
                const url = `${base}${sep}sponsorId=${encodeURIComponent(
                    sponsorVal
                )}`;
                const resp = await callApiOnce(`sponsorValidate:${sponsorVal}`, () => tbGetJson(url));
                const ok = getManagerIsValidFromResp(resp) || (attuidRequiredOn && isManagerAttuidActiveForAttuidFlow(resp));
                const mgrEmailId = (resp?.emailId || resp?.email || "").trim();
                const mgrIdAtCall = form1.webphoneManagerId;
                const sponsorEmailId = (resp?.emailId || resp?.email || "").trim();
                const sponsorIdAtCall = sponsorVal;
                if (!cancelled) {
                    if (sponsorEmailId) {
                        setForm1((prev) =>
                            prev?.sponsor === sponsorIdAtCall
                                ? { ...prev, sponsorEmail: sponsorEmailId, sponsor_email: sponsorEmailId }
                                : prev
                        );
                    }
                    setSponsorValidation({
                        status: ok ? "ok" : "error",
                        message: ok ? "" : "Sponsor not found in Hinge"
                    });
                }
            } catch (err) {
                if (!cancelled) {
                    setSponsorValidation({
                        status: "error",
                        message: "Sponsor validation failed"
                    });
                }
            }
        }, 2000);
        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [form1.sponsor]);

    useEffect(() => {
        if (!ENABLE_WEBPHONE_VALIDATION || !TB_HINGE_WEBPHONE_VALIDATE_URL) return;
        const managerVal = String(form1.webphoneManagerId || "").trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const hasFrontendManagerError = !managerVal || emailRegex.test(managerVal) || managerVal.length > DTVUID_MAX_LEN;

        if (hasFrontendManagerError) {
            setWebphoneValidation({ status: "idle", message: "" });
            setForm1((prev) => ({ ...prev, webphoneManagerEmail: "", webphone_manager_email: "", webphone_manager_attuid: "" }));
            return;
        }

        const cached = readWebphoneManagerCache(managerVal);
        if (cached?.response) {
            const resp = cached.response;
            const ok = getManagerIsValidFromResp(resp) || (attuidRequiredOn && isManagerAttuidActiveForAttuidFlow(resp));
            const mgrEmailId = (resp?.emailId || resp?.email || "").trim();
            const mgrAttuid = getManagerAttuidFromResp(resp);
            const attuidMissing = attuidRequiredOn && !isManagerAttuidActiveForAttuidFlow(resp);
            setWebphoneValidation({
                status: ok ? "ok" : "error",
                message: ok ? "" : "Webphone Manager not found in Hinge"
            });
            setFieldErrors((prev) => ({
                ...prev,
                webphoneManagerId: attuidMissing ? WEBPHONE_MANAGER_ATTUID_INACTIVE_MSG : ([WEBPHONE_MANAGER_ATTUID_INACTIVE_MSG, "webphone manager doesn't have attuid"].includes(prev?.webphoneManagerId) ? "" : prev?.webphoneManagerId || "")
            }));
            setForm1((prev) => ({
                ...prev,
                webphoneManagerEmail: ok && mgrEmailId ? mgrEmailId : "",
                webphone_manager_email: ok && mgrEmailId ? mgrEmailId : "",
                webphone_manager_attuid: mgrAttuid
            }));
            return;
        }
        let cancelled = false;
        setWebphoneValidation({ status: "checking", message: "" });
        const handle = setTimeout(async () => {
            try {
                const base = TB_HINGE_WEBPHONE_VALIDATE_URL;
                const sep = base.includes("?") ? "&" : "?";
                const url = `${base}${sep}mgrDtvId=${encodeURIComponent(
                    managerVal
                )}`;
                const resp = await callApiOnce(`webphoneValidate:${managerVal}`, () => tbGetJson(url));
                writeWebphoneManagerCache(managerVal, resp);
                const ok = getManagerIsValidFromResp(resp) || (attuidRequiredOn && isManagerAttuidActiveForAttuidFlow(resp));
                const mgrEmailId = (resp?.emailId || resp?.email || "").trim();
                const mgrAttuid = getManagerAttuidFromResp(resp);
                const mgrIdAtCall = managerVal;
                if (!cancelled) {
                    const attuidMissing = attuidRequiredOn && !isManagerAttuidActiveForAttuidFlow(resp);
                    if (mgrEmailId) {
                        setForm1((prev) =>
                            prev?.webphoneManagerId === mgrIdAtCall
                                ? { ...prev, webphoneManagerEmail: mgrEmailId, webphone_manager_email: mgrEmailId, webphone_manager_attuid: mgrAttuid }
                                : prev
                        );
                    } else {
                        setForm1((prev) =>
                            prev?.webphoneManagerId === mgrIdAtCall
                                ? { ...prev, webphone_manager_attuid: mgrAttuid }
                                : prev
                        );
                    }
                    setFieldErrors((prev) => ({
                        ...prev,
                        webphoneManagerId: attuidMissing ? WEBPHONE_MANAGER_ATTUID_INACTIVE_MSG : ([WEBPHONE_MANAGER_ATTUID_INACTIVE_MSG, "webphone manager doesn't have attuid"].includes(prev?.webphoneManagerId) ? "" : prev?.webphoneManagerId || "")
                    }));
                    setWebphoneValidation({
                        status: ok ? "ok" : "error",
                        message: ok
                            ? ""
                            : "Webphone Manager not found in Hinge"
                    });
                }
            } catch (err) {
                if (!cancelled) {
                    setWebphoneValidation({
                        status: "error",
                        message: "Webphone validation failed"
                    });
                }
            }
        }, 2000);
        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [form1.webphoneManagerId, attuidRequiredOn]);
    // ===== S2P CONTRACT VALIDATION (optional / debounced) =====
    useEffect(() => {
        if (!ENV.S2P_CONTRACT_VALIDATION_URL) return;
        const v = (form1.vendor || "").trim();
        const c = (form1.contractPo || "").trim();
        if (!c) { setContractValidation({ status: "idle", message: "" }); return; }
        let cancelled = false;
        setContractValidation({ status: "checking", message: "" });
        const t = setTimeout(async () => {
            try {
                const payload = { supplier: v || undefined, contractNumber: c };
                const qs = new URLSearchParams(Object.fromEntries(Object.entries(payload).filter(([_, val]) => val !== undefined))).toString();
                const resp = await callApiOnce(`contractValidate:${(form1.vendor || "").trim()}::${(form1.contractPo || "").trim()}`, () => getJson(`${ENV.S2P_CONTRACT_VALIDATION_URL}?${qs}`));
                const ok = getManagerIsValidFromResp(resp) || (attuidRequiredOn && isManagerAttuidActiveForAttuidFlow(resp));
                if (!cancelled) setContractValidation({ status: ok ? "ok" : "error", message: ok ? "" : "Contract not valid" });
            } catch (e) {
                if (!cancelled) setContractValidation({ status: "error", message: "Contract validation failed" });
            }
        }, 1500);
        return () => { cancelled = true; clearTimeout(t); };
    }, [form1.contractPo, form1.vendor]);

    // ===== S2P PURCHASE ORDER VALIDATION (optional / debounced) =====
    useEffect(() => {
        if (!ENV.S2P_PURCHASEORDER_VALIDATION_URL) return;
        const v = (form1.vendor || "").trim();
        const p = (form1.poNum || "").trim();
        if (!p) { setPoValidation({ status: "idle", message: "" }); return; }
        let cancelled = false;
        setPoValidation({ status: "checking", message: "" });
        const t = setTimeout(async () => {
            try {
                const payload = { supplier: v || undefined, orderNumber: p };
                const qs = new URLSearchParams(Object.fromEntries(Object.entries(payload).filter(([_, val]) => val !== undefined))).toString();
                const resp = await getJson(`${ENV.S2P_PURCHASEORDER_VALIDATION_URL}?${qs}`);
                const ok = getManagerIsValidFromResp(resp) || (attuidRequiredOn && isManagerAttuidActiveForAttuidFlow(resp));
                if (!cancelled) setPoValidation({ status: ok ? "ok" : "error", message: ok ? "" : "Purchase Order not valid" });
            } catch (e) {
                if (!cancelled) setPoValidation({ status: "error", message: "Purchase Order validation failed" });
            }
        }, 1500);
        return () => { cancelled = true; clearTimeout(t); };
    }, [form1.poNum, form1.vendor]);

    // ===== S2P PO LINE VALIDATION (optional / debounced) =====
    useEffect(() => {
        if (!ENV.S2P_POLINES_VALIDATION_URL) return;
        const v = (form1.vendor || "").trim();
        const p = (form1.poNum || "").trim();
        const l = (form1.lineNum || "").trim();
        if (!l) { setLineValidation({ status: "idle", message: "" }); return; }
        let cancelled = false;
        setLineValidation({ status: "checking", message: "" });
        const t = setTimeout(async () => {
            try {
                const payload = { orderNumber: p || undefined, vendor: v || undefined, lineNumber: l };
                const qs = new URLSearchParams(Object.fromEntries(Object.entries(payload).filter(([_, val]) => val !== undefined))).toString();
                const resp = await getJson(`${ENV.S2P_POLINES_VALIDATION_URL}?${qs}`);
                const ok = getManagerIsValidFromResp(resp) || (attuidRequiredOn && isManagerAttuidActiveForAttuidFlow(resp));
                if (!cancelled) setLineValidation({ status: ok ? "ok" : "error", message: ok ? "" : "PO Line not valid" });
            } catch (e) {
                if (!cancelled) setLineValidation({ status: "error", message: "PO Line validation failed" });
            }
        }, 1500);
        return () => { cancelled = true; clearTimeout(t); };
    }, [form1.lineNum, form1.vendor, form1.poNum]);


    // EMAIL_MAX_LEN is defined at module scope
    const runFieldValidation = (name, value) => {
        let msg = "";
        switch (name) {
            case "firstName": {
                const v = String(value || "").trim();
                if (!v) { msg = ""; break; }
                if (!FIRST_LAST_NAME_REGEX.test(v)) {
                    msg = "First Name must be 2-50 characters, start with a letter, and only contain letters, spaces, apostrophe ('), hyphen (-), or period (.)";
                }
                break;
            }
            case "lastName": {
                const v = String(value || "").trim();
                if (!v) { msg = ""; break; }
                if (!FIRST_LAST_NAME_REGEX.test(v)) {
                    msg = "Last Name must be 2-50 characters, start with a letter, and only contain letters, spaces, apostrophe ('), hyphen (-), or period (.)";
                }
                break;
            }
            case "email": {
                const v = String(value || "").trim();
                if (!v) { msg = ""; break; }
                if (v.length > EMAIL_MAX_LEN) {
                    msg = `Email must be ${EMAIL_MAX_LEN} characters or less`;
                    break;
                }
                if (!EMAIL_REGEX.test(v)) {
                    msg = "Enter a valid email (e.g. name@domain.com)";
                }
                break;
            }
            case "phone": {
                const v = String(value || "").trim();
                if (!v) { msg = ""; break; }

                const isUsPhone = isUnitedStatesSelected(form1.country);
                if (isUsPhone) {
                    if (v.length >= 1 && !/^[2-9]/.test(v)) {
                        msg = "Invalid phone. 1st digit must be 2-9.";
                        break;
                    }
                    if (v.length >= 4 && !/^[2-9][0-9]{2}[2-9]/.test(v)) {
                        msg = "Invalid phone. 4th digit must be 2-9.";
                        break;
                    }
                    if (v.length !== 10) {
                        msg = "Phone must be 10 digits";
                        break;
                    }
                    if (!/^[2-9][0-9]{2}[2-9][0-9]{6}$/.test(v)) msg = "Invalid phone. 1st & 4th digits must be 2-9.";
                    break;
                }

                if (!/^\d+$/.test(v)) {
                    msg = "Phone must contain digits only (no spaces or special characters).";
                    break;
                }
                if (v.length < 10) {
                    msg = "Phone must be at least 10 digits.";
                    break;
                }
                if (v.length > 15) {
                    msg = "Phone must be 15 digits or less";
                }
                break;
            }
            case "sponsor":
            case "webphoneManagerId": {
                const idVal = String(value || "").trim();
                if (!idVal) { msg = ""; break; }
                if (idVal.length > DTVUID_MAX_LEN) {
                    msg = "Must be 10 characters or less.";
                    break;
                }
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (emailRegex.test(idVal)) {
                    const fieldLabel = name === "sponsor" ? "Sponsor DTVUID" : "Webphone Manager DTVUID";
                    msg = `Email not allowed.`;
                    break;
                }
                // Webphone Manager cannot be the edited record's own DTVID (all roles except FTE Nominator).
                if (name === "webphoneManagerId" && isEditRoute) {
                    // const isFteNominator = String(sessionStorage.getItem("tb_role") || "")
                    //     .trim()
                    //     .toLowerCase() === "fte nominator";
                    const editedRecordDtvid = String(
                        editData?.dtvId || editData?.dtv_id || form1.dtvId || ""
                    ).trim();
                    if (
                        // !isFteNominator &&
                        editedRecordDtvid &&
                        idVal.toLowerCase() === editedRecordDtvid.toLowerCase()
                    ) {
                        msg = "Webphone Manager DTVUID cannot be the same as the record's DTVID.";
                    }
                }
                break;
            }

            case "securityId": {
                if (isSecurityLocked) { msg = ""; break; }
                if (isSecurityLocked) break;
                const digits = clampDigitsToFormat(isUS, extractDigits(value, isUS));
                if (!digits) msg = isUS ? "Please provide SSN" : requiredError("securityId");
                else if (digits.length !== maxDigitsFor(isUS))
                    msg = isUS ? "SSN must be 9 digits" : "Security ID must be 8 digits";
                else if (!isUS) {
                    const mmddMsg = validateSecurityIdMmdd8(digits);
                    if (mmddMsg) msg = mmddMsg;
                }
                break;
            }
            case "validateSecurity": {
                if (isSecurityLocked) { msg = ""; break; }
                if (isSecurityLocked) break;
                const d1 = clampDigitsToFormat(isUS, extractDigits(form1.securityId, isUS));
                const d2 = clampDigitsToFormat(isUS, extractDigits(value, isUS));
                if (!d2) msg = isUS ? "Please provide Confirm SSN" : requiredError("validateSecurity");
                else if (d2.length !== maxDigitsFor(isUS))
                    msg = isUS ? "Confirm SSN must be 9 digits" : "Confirm Security ID must be 8 digits";
                else {
                    if (!isUS) {
                        const mmddMsg = validateSecurityIdMmdd8(d2);
                        if (mmddMsg) {
                            msg = mmddMsg;
                            break;
                        }
                    }
                    if (d1 && d2 !== d1) msg = isUS ? "Confirm SSN must match SSN" : "Confirm Security ID must match Security ID";
                }
                break;
            }
            case "contractPo":
                if (String(value || "").length > CONTRACT_FIELDS_MAX_LEN) {
                    msg = "Contract # must be 50 characters or less.";
                    break;
                }
                //if (value && !/^[A-Za-z0-9]+$/.test(value)) {
                //  msg = "Contract # can contain only letters and numbers";
                //}
                if (value && !/^[A-Za-z0-9\-_.\\/#&@+]+$/.test(value)) {
                    msg = "Contract # can contain only letters, numbers, and common special characters (- _ . / # & @ +)";
                }
                break;
            case "poNum":
                if (String(value || "").length > CONTRACT_FIELDS_MAX_LEN) {
                    msg = "PO # must be 50 characters or less.";
                    break;
                }
                if (value && !/^[A-Za-z0-9\-_.\\/#&@+]+$/.test(value)) {
                    msg = "PO # can contain only letters and numbers";
                }
                break;
            case "lineNum":
                if (String(value || "").length > CONTRACT_FIELDS_MAX_LEN) {
                    msg = "Line # must be 50 characters or less.";
                    break;
                }
                if (value && !/^\d+$/.test(value)) {
                    msg = "Line # can contain digits only";
                }
                break;
            case "workerTypeTax":
                if (attuidRequiredOn && !value) {
                    msg = requiredError("workerTypeTax");
                }
                break;
            case "nonBillable":
                if (!value) {
                    msg = requiredError("nonBillable");
                }
                break;
            case "attRoleKey":
                if (attuidRequiredOn && !value) {
                    msg = requiredError("attRoleKey");
                }
                break;
            default:
                break;
        }
        setFieldErrors((prev) => ({
            ...prev,
            [name]: msg
        }));
        return msg;
    };

    const validateAll = () => {
        if (isEditRoute && !lookupSecExistFlag && String(editData?.dtvId || editData?.dtv_id || form1.dtvId || "").trim()) {
            toast.error("Loading Security ID details. Please wait a moment.", { toastId: "onb-security-lookup-pending" });
            return false;
        }

        const required = [
            "firstName",
            "lastName",
            "email",
            "sponsor",
            "webphoneManagerId",
            "country",
            "occupancyCode",
            "startDate",
            "endDate"
        ];
        if (isStateRequired) required.push("state");
        if (isCityRequired) required.push("city");
        // In edit/prefill flows, SSN is locked and should not be required/validated
        if (!isSecurityLocked) {
            required.push("securityId", "validateSecurity");
        }
        if (attuidRequiredOn) {
            required.push("citizenship");
            required.push("role");
            required.push("workerTypeTax");
        }
        required.push("nonBillable");
        const newErrors = {};
        const getValue = (field) => {
            if (field === "startDate" || field === "endDate") return form1[field];
            return form1[field] || "";
        };
        required.forEach((f) => {
            if (isSecurityLocked && (f === "securityId" || f === "validateSecurity")) return;
            const value = getValue(f);
            if (!value) {
                newErrors[f] = requiredError(f);
            } else {
                newErrors[f] = runFieldValidation(f, value);
            }
        });

        const phoneVal = String(form1.phone || "").trim();
        if (phoneVal) {
            newErrors.phone = runFieldValidation("phone", phoneVal);
        }

        ["contractPo", "poNum", "lineNum"].forEach((f) => {
            const v = form1[f];
            if (v) {
                newErrors[f] = runFieldValidation(f, v);
            }
        });
        setFieldErrors(newErrors);
        const hasError = Object.values(newErrors).some((m) => m);
        if (hasError) {
            toast.error("Please fix the highlighted errors before continuing.", { toastId: "onb-fix-errors" });
        }
        return !hasError;
    };

    const ensureHingeValidationOk = () => {
        if (ENABLE_SPONSOR_VALIDATION && form1.sponsor) {
            if (sponsorValidation.status === "idle") {
                toast.error(
                    "Sponsor validation not completed yet. Please wait a few seconds."
                );
                return false;
            }
            if (sponsorValidation.status === "error") {
                toast.error("Sponsor is invalid per Hinge. Please correct it.");
                return false;
            }
            if (sponsorValidation.status === "checking") {
                toast.error(
                    "Sponsor validation is in progress. Please wait a moment."
                );
                return false;
            }
        }
        if (ENABLE_WEBPHONE_VALIDATION && form1.webphoneManagerId) {
            if (webphoneValidation.status === "idle") {
                toast.error(
                    "Webphone Manager validation not completed yet. Please wait a few seconds."
                );
                return false;
            }
            if (webphoneValidation.status === "error") {
                toast.error(
                    "Webphone Manager is invalid per Hinge. Please correct it."
                );
                return false;
            }
            if (webphoneValidation.status === "checking") {
                toast.error(
                    "Webphone Manager validation is in progress. Please wait a moment."
                );
                return false;
            }
        }
        if (attuidRequiredOn) {
            const cached = readWebphoneManagerCache(form1.webphoneManagerId);
            const managerValidationResp = cached?.response || null;
            const hasActiveManagerAttuid = isManagerAttuidActiveForAttuidFlow(managerValidationResp) || (
                !managerValidationResp &&
                Boolean(String(form1.webphone_manager_attuid || "").trim()) &&
                !["false", "null", "undefined"].includes(String(form1.webphone_manager_attuid || "").trim().toLowerCase())
            );
            if (!hasActiveManagerAttuid) {
                setFieldErrors((prev) => ({ ...(prev || {}), webphoneManagerId: WEBPHONE_MANAGER_ATTUID_INACTIVE_MSG }));
                toast.error(WEBPHONE_MANAGER_ATTUID_INACTIVE_MSG);
                return false;
            }
        }
        return true;
    };

    const buildCommonPayload = (statusLabel, buttonLabel) => {
        const finalStatus = statusLabel;
        const finalButton =
            buttonLabel || (editData && editData.status ? editData.status : statusLabel);

        const npwIdFromRouteOrPropRaw =
            draftNpwId || (isEditRoute ? (effectiveNpwId || editData?.npw_id) : null) || undefined;

        // Only send npw_id when it is numeric; otherwise send null (ex: "DTVID=pt3665.uat" must never reach backend bigint parsing).
        const npwIdFromRouteOrProp = isNumericNpwId(npwIdFromRouteOrPropRaw) ? String(npwIdFromRouteOrPropRaw).trim() : null;

        // ✅ STRICT: submitter identity ONLY from logged-in session
        const submitterNameSess = (sessionStorage.getItem("tb_name") || name || "").trim();
        const submitterEmailSess = (sessionStorage.getItem("tb_email") || email || "").trim();
        const roleSess = (sessionStorage.getItem("tb_role") || "").trim();
        const approverSess = (sessionStorage.getItem("approver") || approver || "").trim();
        const submitterDtvid = (sessionStorage.getItem("tb_dtvid") || "").trim();

        // If lookup API returned ATTUID, include it in payload (create + edit).
        const attuidPayload = String(
            lookupAttuid || form1?.attuid || editData?.attuid || editData?.attUid || editData?.attUID || editData?.att_uid || ""
        ).trim();
        const cachedManager = readWebphoneManagerCache(form1.webphoneManagerId);
        const webphoneManagerAttuid = String(
            form1?.webphone_manager_attuid ||
            editData?.webphone_manager_attuid ||
            editData?.webphoneManagerAttuid ||
            getManagerAttuidFromResp(cachedManager?.response)
        ).trim();

        const secDigits = clampDigitsToFormat(isUS, extractDigits(form1.securityId, isUS));

        // For Non-US Security ID: send "F" + 8 digits to backend to make it 9 chars consistently.
        // For US SSN: send 9 digits as-is.
        const secPayload = isUS ? secDigits : (secDigits ? `F${secDigits}` : secDigits);

        const safeForm1 = {
            ...form1,
            validateSecurity: undefined, // UI only
            startDate: serializeDate(form1.startDate),
            endDate: serializeDate(form1.endDate)
        };
        if (webphoneManagerAttuid) safeForm1.webphone_manager_attuid = webphoneManagerAttuid;
        // Only include securityId when we intend to submit it.
        if (secPayload) safeForm1.securityId = secPayload;

        /**
         * Route Submit to NEW vs EDIT based on NPW existence
         * Rule: if npw_id exists -> EDIT (PUT/POST), else -> NEW (POST)
         */
        const hasExistingNpw = () => {
            const effective = draftNpwId || effectiveNpwId || editData?.npw_id;
            return isNumericNpwId(effective);
        };

        // Only send Security ID/SSN when user is actually editing it, and only if it changed.
        // In edit flows, disabling Edit must also disable validation and omit the field from payload.
        const securityUnchanged = secDigits === securityInitialDigits;
        const shouldOmitSecurityId = isSecurityEditRecord && (!securityEditEnabled || securityUnchanged);
        if (shouldOmitSecurityId) delete safeForm1.securityId;

        const callNewOrEdit = async (payload) => {
            if (hasExistingNpw()) return apiSubmitEdit(payload); // POST/PUT
            return apiSubmitNew(payload); // POST
        };

        /*TODO:pentest*/
        // //body v = hashed-roleSess
        const dtvRaw = sessionStorage.getItem("tb_dtvid") || "";
        const ts = Date.now();
        const vRaw = `${roleSess}_*v*_${ts}`;
        const dRaw = `${dtvRaw}_*v*_${ts}`;
        const vParam = typeof btoa === "function" ? btoa(vRaw) : window.btoa(vRaw);
        const dParam = typeof btoa === "function" ? btoa(dRaw) : window.btoa(dRaw);

        return {
            workerType,
            form1: safeForm1,
            status: finalStatus,
            /*TODO:pentest*/
            v: vParam,
            d: dParam,
            
            buttonAction: finalButton,
            ...(isReactiveAttuidFlow && finalStatus === "Submitted" ? { reactive_attuid: true } : {}),

            ...(attuidPayload ? { attuid: attuidPayload } : {}),
            ...(webphoneManagerAttuid ? { webphone_manager_attuid: webphoneManagerAttuid } : {}),

            // 👇 Send multiple keys for API compatibility (snake + camel + explicit logged-in keys)
            submitter: submitterEmailSess || undefined,
            submitter_id: submitterEmailSess || undefined,
            loggedInUserEmail: submitterEmailSess || undefined,
            submitterDtvid: submitterDtvid || undefined,

            submitterName: submitterNameSess || undefined,
            submitter_name: submitterNameSess || undefined,
            loggedInUserName: submitterNameSess || undefined,

            approver: editData ? (editData.approver_id || approverSess) : approverSess,

            loggedInUserRole: roleSess,
            nominatorRole: roleSess,

            // npw_id must be numeric; otherwise it must be null so backend bigint parsing doesn't fail.
            npw_id: npwIdFromRouteOrProp,
            npwId: npwIdFromRouteOrProp,

            ...((draftClientRequestId || editData?.client_request_id)
                ? {
                    clientRequestId: draftClientRequestId || editData?.client_request_id || null,
                    client_request_id: draftClientRequestId || editData?.client_request_id || null
                }
                : {})
        };
    };
    const handleCancel = () => {
        const ok = window.confirm("Cancel onboarding and return to Dashboard? Unsaved changes will be lost.");
        if (!ok) return;
        navigate("/dashboard");
    };

    // 🔹 Save Draft: NO validation checks now
    const saveDraft = async () => {

        // Draft rule: require Security ID entry when Security is editable (NEW flow).
        // If Security is locked (editing existing NPW / loaded by npw_id or DTVID-prefill), we do NOT block Draft.
        if (!isSecurityLocked) {
            const _isUS = attuidRequiredOn && isUnitedStatesSelected(selectedCountry?.label || form1.country || "");
            const _secDigits = clampDigitsToFormat(_isUS, extractDigits(form1.securityId, _isUS));
            const _need = maxDigitsFor(_isUS);

            if (!_secDigits || _secDigits.length !== _need) {
                setFieldErrors((prev) => ({
                    ...(prev || {}),
                    securityId: `${_isUS ? "SSN" : "Security ID"} must be ${_need} digits`,
                    validateSecurity: ""
                }));
                toast.error(`Please enter a valid ${_need}-digit ${_isUS ? "SSN" : "Security ID"} before saving Draft.`);
                try { securityRef?.current?.focus(); } catch { }
                return;
            }

            // Extra validation for non-US Security ID: MMDDXXXX where MM/DD are valid.
            if (!_isUS) {
                const mmddMsg = validateSecurityIdMmdd8(_secDigits);
                if (mmddMsg) {
                    setFieldErrors((prev) => ({
                        ...(prev || {}),
                        securityId: mmddMsg,
                        validateSecurity: ""
                    }));
                    toast.error("Please enter a valid Security ID (MMDDXXXX).", { toastId: "onb-securityid-mmdd" });
                    try { securityRef?.current?.focus(); } catch { }
                    return;
                }
            }
        }

        // Guard: ensure logged-in identity is present
        const _nm = (sessionStorage.getItem("tb_name") || name || "").trim();
        const _em = (sessionStorage.getItem("tb_email") || email || "").trim();
        if (!_nm || !_em) {
            toast.error("You're signed in without name/email in session. Please sign out and sign in again.");
            return;
        }
        const payload = buildCommonPayload("Draft", "Draft");
        try {
            setLoading(true);
            const resp = await callInFlightOnce("saveDraft", () => apiSaveDraft(payload));
            const respNpw = resp?.npw_id || resp?.npwId || resp?.data?.npw_id || resp?.data?.npwId;
            const respClient = resp?.client_request_id || resp?.clientRequestId || resp?.data?.client_request_id || resp?.data?.clientRequestId;
            if (isEditRoute) {
                if (respNpw) {
                    const v = String(respNpw).trim();
                    if (/^\d+$/.test(v)) {
                        setDraftNpwId(v);
                        sessionStorage.setItem("tb_draft_npw_id", v);
                    } else {
                        // Never persist non-numeric IDs (ex: "DTVID=...") as draft NPW IDs.
                        setDraftNpwId(null);
                        try { sessionStorage.removeItem("tb_draft_npw_id"); } catch { }
                    }
                }
                if (respClient) {
                    const v = String(respClient);
                    setDraftClientRequestId(v);
                    sessionStorage.setItem("tb_draft_client_request_id", v);
                }
            }
            if (isEditRoute) {
                setEditData((prev) => ({
                    ...(prev || {}),
                    ...form1,
                    worker_type: "Non-Payroll Worker",
                    validateSecurity: form1.securityId,
                    status: "Draft",
                    npw_id: respNpw ? String(respNpw) : prev?.npw_id || draftNpwId || null,
                    client_request_id: respClient
                        ? String(respClient)
                        : prev?.client_request_id || draftClientRequestId || null,
                }));
            } else {
                // In NEW mode, do not keep ids in editData (prevents updating the same record).
                setEditData(null);
            }
            toast.success("Draft Saved");
            // On edit routes, keep the current values visible.
            if (!isEditRoute) {
                // Important: clear cached draft IDs so the next Draft/Submit creates a NEW record
                // without requiring a page reload.
                setDraftNpwId(null);
                setDraftClientRequestId(null);
                try {
                    sessionStorage.removeItem("tb_draft_npw_id");
                    sessionStorage.removeItem("tb_draft_client_request_id");
                } catch { }

                setForm1(initialVal);
                setFieldErrors({});
                setStep(1);
                setSelectedState(null);
                setSelectedCity(null);
                setSelectedCountry(null);
                setCitizen(null);
                SetSelectedTimeZone(null);
                setSupplierLocked(false);
            }
        } catch (e) {
            console.error(e);
            toast.error("Failed to save draft.");
        } finally {
            setLoading(false);
        }
    };

    const submitAll = async () => {
        // Guard: ensure logged-in identity is present
        const _nm = (sessionStorage.getItem("tb_name") || name || "").trim();
        const _em = (sessionStorage.getItem("tb_email") || email || "").trim();
        if (!_nm || !_em) {
            toast.error("You're signed in without name/email in session. Please sign out and sign in again.");
            return;
        }
        if (!ensureSupplierValidationReady()) return;
        if (!validateAll()) {
            toast.error("Please fix validation errors before submitting.");
            return;
        }
        if (!ensureHingeValidationOk()) return;
        if (requiresCpValidateBeforeSubmit && cpValidation.status !== "passed") {
            toast.error("Please click Validate and resolve all CP errors before submitting.");
            return;
        }
        const payload = buildCommonPayload("Submitted", null);
        try {
            setLoading(true);
            setSubmitting(true);
            const existingNpwId = draftNpwId || effectiveNpwId || editData?.npw_id || undefined;
            const submitUrl = existingNpwId ? ONBOARDING_EDIT_URL : ONBOARDING_NEW_URL;

            // Helpful debug (shows exact endpoint used in DevTools)
            // eslint-disable-next-line no-console
            console.info("[OnboardingNominator] submitting to:", submitUrl, { existingNpwId: !!existingNpwId });

            const resp = existingNpwId
                ? await apiSubmitEdit(payload)
                : await apiSubmitNew(payload);

            // Guardrails: do NOT show success unless we got a real response.
            const looksEmptyObject =
                resp && typeof resp === "object" && !Array.isArray(resp) && Object.keys(resp).length === 0;
            const looksHtml = typeof resp === "string" && /^\s*</.test(resp);

            if (resp == null || resp === "" || looksEmptyObject) {
                throw new Error("Empty response from onboarding API");
            }
            if (looksHtml) {
                throw new Error(
                    "Received HTML instead of JSON. Check ENV.TB_BASE / ONBOARDING_*_URL (may be pointing to the UI host instead of the API/proxy)."
                );
            }

            const msg = existingNpwId
                ? "Request updated successfully"
                : "Request submitted successfully!";
            // Show toast and then redirect. (Immediate navigation unmounts ToastContainer)
            toastThenNavigate(msg, "success", OVERVIEW_PATH);

            // After a successful submit on the NEW route, clear IDs + edit state
            // so the next submit creates a brand-new record without reload.
            if (!isEditRoute) {
                setEditData(null);
            }

            // Always clear draft tracking once final submit succeeds.
            setDraftNpwId(null);
            setDraftClientRequestId(null);
            try {
                sessionStorage.removeItem("tb_draft_npw_id");
                sessionStorage.removeItem("tb_draft_client_request_id");
            } catch { }
            // Navigation is handled by toastThenNavigate.

        } catch (e) {
            console.error(e);
            const messages = getCpErrorMessages(e, { max: 12 });
            if (messages.length) {
                messages.forEach((msg) => toast.error(msg));
            } else {
                toast.error("Failed to submit.");
            }
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    const validateBeforeSubmit = async () => {
        if (!requiresCpValidateBeforeSubmit) {
            setCpValidation({ status: "passed", message: "Validation not required" });
            toast.success("Validation not required for this onboarding flow.");
            return;
        }
        if (!ensureSupplierValidationReady()) return;
        if (!validateAll()) {
            toast.error("Please fix validation errors before validating.");
            return;
        }
        if (!ensureHingeValidationOk()) return;

        const validateNpwIdRaw = draftNpwId || effectiveNpwId || editData?.npw_id || editData?.npwId || "";
        const validateNpwId = isNumericNpwId(validateNpwIdRaw) ? String(validateNpwIdRaw).trim() : "";
        const pick = (...vals) => {
            for (const val of vals) {
                const s = String(val ?? "").trim();
                if (s && s.toLowerCase() !== "null" && s.toLowerCase() !== "undefined") return s;
            }
            return "";
        };
        const attRoleKeyForValidate = pick(form1?.attRoleKey, form1?.att_role_key, editData?.attRoleKey, editData?.att_role_key);
        const profileFromAttRoleKey = (() => {
            const idx = String(attRoleKeyForValidate || "").indexOf("||");
            return idx >= 0 ? String(attRoleKeyForValidate).slice(idx + 2).trim() : "";
        })();
        const roleFromAttRoleKey = (() => {
            const idx = String(attRoleKeyForValidate || "").indexOf("||");
            return idx >= 0 ? String(attRoleKeyForValidate).slice(0, idx).trim() : "";
        })();
        const derivedProfileFromCatalog = (() => {
            const cpRoleId = pick(form1?.attRoleCpRoleId, form1?.att_role_cp_role_id, roleFromAttRoleKey, form1?.attRoleKey);
            if (!cpRoleId) return "";
            for (const role of hingeRoleCatalog || []) {
                for (const cp of role?.cinglepointRoles || []) {
                    if (String(cp?.cpRoleId || "").trim() !== cpRoleId) continue;
                    for (const profile of cp?.cpProfiles || []) {
                        const cpProfileId = String(profile?.cpProfileId || "").trim();
                        if (cpProfileId) return cpProfileId;
                    }
                }
            }
            return "";
        })();
        const cpProfileId = pick(
            form1?.attRoleCpProfileId,
            form1?.att_role_cp_profile_id,
            form1?.cinglepointProfileId,
            form1?.singlepointProfileId,
            editData?.attRoleCpProfileId,
            editData?.att_role_cp_profile_id,
            editData?.cinglepointProfileId,
            editData?.singlepointProfileId,
            profileFromAttRoleKey,
            derivedProfileFromCatalog
        );
        if (!cpProfileId) {
            const msg = "CP Profile ID is required for validation.";
            setCpValidation({ status: "failed", message: msg });
            toast.error(msg);
            return;
        }
        const ssnSecId = extractDigits(
            pick(
                form1?.securityId,
                form1?.validateSecurity,
                form1?.confirmSecurityId,
                form1?.security_id_plaintext,
                form1?.securityIdPlaintext
            ),
            isUS
        );

        const requestDetail = {
            cp_approver_attuid: pick(form1?.sponsor, form1?.webphone_manager_attuid, sessionStorage.getItem("tb_attuid")),
            first_name: pick(form1?.firstName, editData?.first_name),
            last_name: pick(form1?.lastName, editData?.last_name),
            email: pick(form1?.email, editData?.email),
            phone: pick(form1?.phone, editData?.phone, editData?.phone_number),
            phone_number: pick(form1?.phone, editData?.phone, editData?.phone_number),
            country: pick(form1?.citizenship, editData?.citizenship, form1?.country, editData?.country),
            countryOfWorking: pick(form1?.country, editData?.country),
            attuid: pick(form1?.attuid, editData?.attuid),
            dtvid: pick(form1?.dtvId, effectiveDtvid, editData?.dtv_id) || generateTbFallbackDtvid(),
            worker_type_tax: pick(form1?.workerTypeTax, editData?.w2_1099),
            webphone_manager_attuid: pick(form1?.webphone_manager_attuid, form1?.webphoneManagerId, editData?.webphone_manager_attuid),
            cp_role_id: pick(form1?.attRoleCpRoleId, form1?.att_role_cp_role_id, editData?.att_role_cp_role_id),
            role: pick(form1?.role, editData?.role),
            cp_profile_id: cpProfileId,
            sow: cpProfileId,
            security_id_hash: pick(form1?.security_id_hash, form1?.securityIdHash) || null,
            security_id_plaintext: null,
            securityId: null,
            security_id: null,
            ssn: null,
            ssn_plain: null,
            secid: null,
            sec_id: null,
            start_date: serializeDate(form1?.startDate) || pick(editData?.start_date, new Date().toISOString().slice(0, 10)),
            end_date: serializeDate(form1?.endDate) || pick(editData?.end_date) || null,
            comments: "Validation from UI no-npw flow",
            fte_nominated: false,
            offboard_by_attuid: false,
            ...(ssnSecId ? { ssnSecId } : {}),
        };

        // if(!requestDetail.dtvid || requestDetail.dtvid === "null" || requestDetail.dtvid === "undefined" || requestDetail.dtvid === '') {
        //     requestDetail.dtvid = generateTbFallbackDtvid();
        // }
        const validateStatusKey = String(editData?.personStatus ?? editData?.person_status ?? editData?.status ?? "")
            .trim()
            .toLowerCase()
            .replace(/[\s_-]/g, "");
        const shouldSendFullValidatePayload = !validateNpwId || validateStatusKey === "draft";

        const payload = {
            req_type: "action",
            action: "VALIDATE",
            client_request_id: `ui-${Date.now()}-${Math.random().toString(16).slice(2)}`,
            request_detail: requestDetail,
            ...(!shouldSendFullValidatePayload && validateNpwId ? { npw_id: Number(validateNpwId) } : {}),
        };
        try {
            setLoading(true);
            setCpValidation({ status: "checking", message: "Validating with CP API..." });

            const resp = await apiValidateCp(payload);

            const failed = hasApiValidationFailure(resp);
            const messages = getCpErrorMessages(resp, { max: 12 });

            if (failed) {
                if (messages.length) {
                    messages.forEach((msg) => toast.error(msg));
                } else {
                    toast.error("CP validation failed. Please review and correct the data.");
                }
                setCpValidation({ status: "failed", message: messages[0] || "Validation failed" });
                return;
            }

            setCpValidation({ status: "passed", message: "Validation successful" });
            toast.success("Validation successful. You can submit now.");
        } catch (e) {
            const messages = getCpErrorMessages(e, { max: 12 });
            const msg = messages[0] || String(e?.message || e || "Validation failed").trim();
            setCpValidation({ status: "failed", message: msg });
            if (messages.length) {
                messages.forEach((item) => toast.error(item));
            } else {
                toast.error(msg || "CP validation failed.");
            }
        } finally {
            setLoading(false);
        }
    };


    const handleAttuidToggle = () => {
        setForm1((prev) => {
            const currentOn =
                prev.attuidRequired === "Yes" || prev.attuidRequired === "true" || prev.isAttuidRequested === "Yes";
            const nextOn = !currentOn;
            let next = {
                ...prev,
                attuidRequired: nextOn ? "Yes" : "No",
                isAttuidRequested: nextOn ? "Yes" : "No",
                // When ATTUID Required toggles, reset dependent dropdowns so user can re-select cleanly.
                role: "",
                // workerTypeTax: "", // Worker Type is not strictly required when ATTUID is on/off
                // nonBillable: "", // Non-billable is not strictly required when ATTUID is on/off
                attRoleKey: "",
                attRoleDtvRoleId: "",
                attRoleDtvRoleName: "",
                attRoleCpRoleId: "",
                attRoleCpRoleDesc: "",
                attRoleCpProfileId: "",
                attRoleCpProfileDesc: ""
            };
            // If turning ON and a DTV role is already chosen, auto-select ATT role now.
            if (nextOn && prev.role) {
                const auto = autoSelectAttRoleForDtvRole(prev.role);
                if (auto) next = { ...next, ...auto };
            }
            return next;
        });
        setFieldErrors((prev) => ({
            ...prev,
            role: "",
            workerTypeTax: "",
            attRoleKey: "",
            nonBillable: ""
        }));
    };



    // =========================
    // Supplier (S2P) validation hard-block
    // =========================
    const SUPPLIER_VALIDATION_BLOCK_TOAST_ID = "supplier-validation-block";
    const SUPPLIER_VALIDATION_BLOCK_MSG =
        "Supplier Validation has failed, please reload the page";

    const ensureSupplierValidationReady = () => {
        const supplierRef = String(form1?.supplierReference || "").trim();

        // If supplier reference is already present (draft/edit or successful S2P lookup), allow.
        if (supplierRef) return true;

        // If login vendor exists but supplier ref isn't present, treat as failed/unusable.
        // Also block when the S2P lookup is still loading or errored.
        const status = dataLoads?.vendorSupplier?.status || "idle";
        if (loginVendorName || status === "loading" || status === "error") {
            toast.error(SUPPLIER_VALIDATION_BLOCK_MSG, { toastId: SUPPLIER_VALIDATION_BLOCK_TOAST_ID });
            return false;
        }

        // Default: block if supplierRef is missing.
        toast.error(SUPPLIER_VALIDATION_BLOCK_MSG, { toastId: SUPPLIER_VALIDATION_BLOCK_TOAST_ID });
        return false;
    };


    const handleContinue = () => {
        const blocks = blockingLoadsForContinue(dataLoads, { timeoutMs: 20000 });
        if (blocks.length) {
            blocks.forEach((b) => {
                const toastOpts = { toastId: dataLoadToastId(b.status, b.field) };
                if (b.status === "error") toast.error(loadFailMessage(b.field), toastOpts);
                else if (b.status === "timeout") toast.warn(loadTimeoutMessage(b.field), toastOpts);
                else toast.info(loadOngoingMessage(b.field), toastOpts);
            });
            return;
        }
        if (!ensureSupplierValidationReady()) return;
        if (!validateAll()) return;
        if (!ensureHingeValidationOk()) return;
        setStep(step + 1);
    };



    return (
        <AppShell>
            <ToastContainer />
            {loading && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(255, 255, 255, 0.7)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999
                    }}
                >
                    <div style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
                        Loading...
                    </div>
                </div>
            )}

            <div className="ap-page-wrap page-scroll-scope">
                <div className="onb-wrap compact">
                    <h2 className="onb-title">
                        {isReactiveAttuidFlow
                            ? "Reactivate ATTUID"
                            : (dtvPrefillHeadingOverride || editHeadingOverride || (isEditRoute ? "Edit Onboarding Details" : "New Onboarding"))}
                    </h2>

                    <div className="stepper" style={{ marginBottom: 24 }}>
                        <StepDot
                            n={1}
                            active={step === 1}
                            done={step > 1}
                            label="Enter Details"
                        />
                        <StepBar done={step > 1} />
                        <StepDot
                            n={2}
                            active={step === 2}
                            done={step > 2}
                            label="Review & Submit"
                        />
                    </div>

                    {step === 1 && (
                        <div
                            className="grid-3"
                            style={{
                                gridGap: "32px",
                                padding: "24px 40px 40px",
                                alignItems: "flex-start"
                            }}
                        >
                            <div>
                                <Section title="Personal Details:">
                                    <Row
                                        NonMandatory={NonMandatory.includes("firstName")}
                                        label="First Name"
                                    >
                                        <input
                                            value={form1.firstName}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setForm1({
                                                    ...form1,
                                                    firstName: value
                                                });
                                                runFieldValidation("firstName", value);
                                            }}
                                        />
                                        {fieldErrors.firstName && (
                                            <div
                                                style={{
                                                    color: "red",
                                                    fontSize: 12,
                                                    marginTop: 4
                                                }}
                                            >
                                                {fieldErrors.firstName}
                                            </div>
                                        )}
                                    </Row>
                                    <Row
                                        NonMandatory={NonMandatory.includes("lastName")}
                                        label="Last Name"
                                    >
                                        <input
                                            value={form1.lastName}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setForm1({
                                                    ...form1,
                                                    lastName: value
                                                });
                                                runFieldValidation("lastName", value);
                                            }}
                                        />
                                        {fieldErrors.lastName && (
                                            <div
                                                style={{
                                                    color: "red",
                                                    fontSize: 12,
                                                    marginTop: 4
                                                }}
                                            >
                                                {fieldErrors.lastName}
                                            </div>
                                        )}
                                    </Row>
                                    <Row label="Email">
                                        <input
                                            value={form1.email || ""}
                                            type="email"
                                            maxLength={EMAIL_MAX_LEN}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                const next = String(raw || "");
                                                setForm1({
                                                    ...form1,
                                                    email: next
                                                });
                                                // Real-time format validation while typing
                                                runFieldValidation("email", next);
                                            }}
                                            onBlur={() => runFieldValidation("email", form1.email)}
                                        />
                                        {fieldErrors.email && (
                                            <div
                                                style={{
                                                    color: "red",
                                                    fontSize: 12,
                                                    marginTop: 4
                                                }}
                                            >
                                                {fieldErrors.email}
                                            </div>
                                        )}
                                    </Row>
                                    <Row label="Phone" NonMandatory={NonMandatory.includes("phone")}>
                                        <input

                                            type="tel"
                                            placeholder={isUnitedStatesSelected(form1.country) ? "1st & 4th digits between 2-9" : ""}
                                            value={form1.phone || ""}
                                            onChange={(e) => {
                                                const maxPhoneLen = isUnitedStatesSelected(form1.country) ? 10 : 15;
                                                const digitsOnly = String(e.target.value || "")
                                                    .replace(/\D/g, "")
                                                    .slice(0, maxPhoneLen);
                                                setForm1({
                                                    ...form1,
                                                    phone: digitsOnly
                                                });
                                                runFieldValidation("phone", digitsOnly);
                                            }}
                                        />
                                        {fieldErrors.phone && (
                                            <div
                                                style={{
                                                    color: "red",
                                                    fontSize: 12,
                                                    marginTop: 4
                                                }}
                                            >
                                                {fieldErrors.phone}
                                            </div>
                                        )}
                                    </Row>
                                    {Boolean((effectiveDtvid && String(effectiveDtvid).trim()) && !(effectiveNpwId && String(effectiveNpwId).trim())) && (
                                        <>
                                            {Boolean(String(form1?.dtvId || editData?.dtvId || editData?.dtv_id || editData?.dtvAnchorId || "").trim()) && (
                                                <TextRow
                                                    label="DTVID"
                                                    inline
                                                    rdOnly
                                                    NonMandatory
                                                    v={String(form1?.dtvId || editData?.dtvId || editData?.dtv_id || editData?.dtvAnchorId || "").trim()}
                                                    onChange={() => { }}
                                                />
                                            )}
                                            {Boolean(String(form1?.attuid || editData?.attuid || editData?.attUid || editData?.attUID || editData?.att_uid || "").trim()) && (
                                                <TextRow
                                                    label="ATTUID"
                                                    inline
                                                    rdOnly
                                                    NonMandatory
                                                    v={String(form1?.attuid || editData?.attuid || editData?.attUid || editData?.attUID || editData?.att_uid || "").trim()}
                                                    onChange={() => { }}
                                                />
                                            )}
                                        </>
                                    )}
                                    {attuidRequiredOn && (
                                        <Row label="Country of Citizenship" NonMandatory={!attuidRequiredOn}>
                                            <Select
                                                options={(citizenshipOptions && citizenshipOptions.length) ? citizenshipOptions : countryOptions}

                                                value={ctzn}
                                                onChange={(e) => {
                                                    setCitizen(e);
                                                    const val = e?.label || "";
                                                    setForm1({
                                                        ...form1,
                                                        citizenship: val
                                                    });
                                                    runFieldValidation("citizenship", val);
                                                }}
                                                placeholder="Select"
                                                isSearchable
                                                styles={{
                                                    control: (b) => ({
                                                        ...b,
                                                        minHeight: 20,
                                                        fontSize: 14
                                                    }),
                                                    dropdownIndicator: (b) => ({
                                                        ...b,
                                                        padding: 2
                                                    }),
                                                    clearIndicator: (b) => ({
                                                        ...b,
                                                        padding: 2
                                                    }),
                                                    valueContainer: (b) => ({
                                                        ...b,
                                                        padding: "0 6px"
                                                    }),
                                                    input: (b) => ({
                                                        ...b,
                                                        margin: 0,
                                                        padding: 0
                                                    })
                                                }}
                                            />
                                            {fieldErrors.citizenship && (
                                                <div
                                                    style={{
                                                        color: "red",
                                                        fontSize: 12,
                                                        marginTop: 4
                                                    }}
                                                >
                                                    {fieldErrors.citizenship}
                                                </div>
                                            )}
                                        </Row>
                                    )}
                                </Section>

                                <Section title="Project Details:">
                                    <TextRow
                                        label="Supplier Reference"
                                        inline
                                        NonMandatory={NonMandatory.includes(
                                            "supplierReference"
                                        )}
                                        v={form1.supplierReference}
                                        onChange={(v) =>
                                            setForm1({
                                                ...form1,
                                                supplierReference: v
                                            })
                                        }
                                        disabled={supplierLocked}
                                    />
                                    <TextRow
                                        label="Vendor"
                                        inline
                                        rdOnly
                                        v={form1.vendor}
                                        onChange={(v) =>
                                            setForm1({
                                                ...form1,
                                                vendor: v
                                            })
                                        }
                                    />
                                </Section>
                            </div>

                            <div>
                                <Section title="Sponsorship & Role:">
                                    <TextRow
                                        label="DTV Sponsor DTVUID (Owns portal record)"
                                        v={form1.sponsor}
                                        maxLength={DTVUID_MAX_LEN}
                                        onChange={(v) => {
                                            const next = String(v || "").slice(0, DTVUID_MAX_LEN);
                                            setForm1({
                                                ...form1,
                                                sponsor: next
                                            });
                                            runFieldValidation("sponsor", next);
                                        }}
                                        error={
                                            !!fieldErrors.sponsor ||
                                            sponsorValidation.status === "error"
                                        }
                                        errorLabel={fieldErrors.sponsor && (
                                            <div
                                                style={{
                                                    color: "red",
                                                    fontSize: 12,
                                                    marginTop: 4
                                                }}
                                            >
                                                {fieldErrors.sponsor}
                                            </div>
                                        )}
                                    />
                                    {ENABLE_SPONSOR_VALIDATION &&
                                        sponsorValidation.status !== "idle" && (
                                            <div
                                                className="field-hint"
                                                style={{
                                                    fontSize: 12,
                                                    marginTop: 4,
                                                    color:
                                                        sponsorValidation.status ===
                                                            "error"
                                                            ? "red"
                                                            : sponsorValidation.status ===
                                                                "ok"
                                                                ? "green"
                                                                : "#555"
                                                }}
                                            >
                                                {sponsorValidation.status ===
                                                    "checking" &&
                                                    "Validating sponsor..."}
                                                {sponsorValidation.status ===
                                                    "ok" &&
                                                    "Sponsor verified."}
                                                {sponsorValidation.status ===
                                                    "error" &&
                                                    sponsorValidation.message}
                                            </div>
                                        )}

                                    <TextRow
                                        label="Webphone Manager DTVUID (HR line manager)"
                                        v={form1.webphoneManagerId}
                                        maxLength={DTVUID_MAX_LEN}
                                        onChange={(v) => {
                                            const next = String(v || "").slice(0, DTVUID_MAX_LEN);
                                            setForm1({
                                                ...form1,
                                                webphoneManagerId: next,
                                                webphone_manager_attuid: ""
                                            });
                                            runFieldValidation(
                                                "webphoneManagerId",
                                                next
                                            );
                                        }}
                                        error={
                                            !!fieldErrors.webphoneManagerId ||
                                            webphoneValidation.status === "error"
                                        }
                                        errorLabel={null}
                                    />

                                    {(fieldErrors.webphoneManagerId ||
                                        (ENABLE_WEBPHONE_VALIDATION && webphoneValidation.status !== "idle")) && (
                                            <div
                                                className="field-hint"
                                                style={{
                                                    fontSize: 12,
                                                    marginTop: 4,
                                                    color: fieldErrors.webphoneManagerId
                                                        ? "red"
                                                        : webphoneValidation.status === "error"
                                                            ? "red"
                                                            : webphoneValidation.status === "ok"
                                                                ? "green"
                                                                : "#555"
                                                }}
                                            >
                                                {fieldErrors.webphoneManagerId ||
                                                    (webphoneValidation.status === "checking"
                                                        ? "Validating Webphone Manager..."
                                                        : webphoneValidation.status === "ok"
                                                            ? "Webphone Manager verified."
                                                            : webphoneValidation.status === "error"
                                                                ? webphoneValidation.message
                                                                : "")}
                                            </div>
                                        )}
                                    <Row label="Country">
                                        <Select
                                            options={countryOptions}
                                            value={selectedCountry}
                                            onChange={(opt) => {
                                                setSelectedCountry(opt);
                                                setSelectedState(null);
                                                setSelectedCity(null);
                                                setCityOptions([]);
                                                const label = opt?.label || "";
                                                setForm1((p) => ({
                                                    ...p,
                                                    country: label,
                                                    state: "",
                                                    city: ""
                                                }));
                                                runFieldValidation(
                                                    "country",
                                                    label
                                                );
                                            }}
                                            placeholder="Select"
                                            isClearable
                                            styles={{
                                                control: (b) => ({
                                                    ...b,
                                                    minHeight: 20,
                                                    fontSize: 14
                                                }),
                                                dropdownIndicator: (b) => ({
                                                    ...b,
                                                    padding: 2
                                                }),
                                                clearIndicator: (b) => ({
                                                    ...b,
                                                    padding: 2
                                                }),
                                                valueContainer: (b) => ({
                                                    ...b,
                                                    padding: "0 6px"
                                                }),
                                                input: (b) => ({
                                                    ...b,
                                                    margin: 0,
                                                    padding: 0
                                                })
                                            }}
                                        />
                                        {fieldErrors.country && (
                                            <div
                                                style={{
                                                    color: "red",
                                                    fontSize: 12,
                                                    marginTop: 4
                                                }}
                                            >
                                                {fieldErrors.country}
                                            </div>
                                        )}
                                    </Row>

                                    <Row label="State" NonMandatory={!isStateRequired}>
                                        <Select
                                            options={stateOptions}
                                            value={selectedState}
                                            onChange={(opt) => {
                                                setSelectedState(opt);
                                                setSelectedCity(null);
                                                if (!opt) setCityOptions([]);
                                                const label = opt?.label || "";
                                                setForm1((p) => ({
                                                    ...p,
                                                    state: label,
                                                    city: ""
                                                }));
                                                runFieldValidation(
                                                    "state",
                                                    label
                                                );
                                            }}
                                            placeholder="Select"
                                            isClearable
                                            isDisabled={!selectedCountry}
                                            noOptionsMessage={() =>
                                                selectedCountry
                                                    ? "No states found"
                                                    : "Select a country first"
                                            }
                                            styles={{
                                                control: (b) => ({
                                                    ...b,
                                                    minHeight: 20,
                                                    fontSize: 14
                                                }),
                                                dropdownIndicator: (b) => ({
                                                    ...b,
                                                    padding: 2
                                                }),
                                                clearIndicator: (b) => ({
                                                    ...b,
                                                    padding: 2
                                                }),
                                                valueContainer: (b) => ({
                                                    ...b,
                                                    padding: "0 6px"
                                                }),
                                                input: (b) => ({
                                                    ...b,
                                                    margin: 0,
                                                    padding: 0
                                                })
                                            }}
                                        />
                                        {fieldErrors.state && (
                                            <div
                                                style={{
                                                    color: "red",
                                                    fontSize: 12,
                                                    marginTop: 4
                                                }}
                                            >
                                                {fieldErrors.state}
                                            </div>
                                        )}
                                    </Row>

                                    <Row label="City" NonMandatory={!isCityRequired}>
                                        <Select
                                            options={cityOptions}
                                            value={selectedCity}
                                            onChange={handleCityChange}
                                            placeholder="Select"
                                            isClearable
                                            isDisabled={!selectedState}
                                            noOptionsMessage={() =>
                                                selectedState
                                                    ? "No cities found"
                                                    : "Select a state first"
                                            }
                                            styles={{
                                                control: (b) => ({
                                                    ...b,
                                                    minHeight: 20,
                                                    fontSize: 14
                                                }),
                                                dropdownIndicator: (b) => ({
                                                    ...b,
                                                    padding: 2
                                                }),
                                                clearIndicator: (b) => ({
                                                    ...b,
                                                    padding: 2
                                                }),
                                                valueContainer: (b) => ({
                                                    ...b,
                                                    padding: "0 6px"
                                                }),
                                                input: (b) => ({
                                                    ...b,
                                                    margin: 0,
                                                    padding: 0
                                                })
                                            }}
                                        />
                                        {fieldErrors.city && (
                                            <div
                                                style={{
                                                    color: "red",
                                                    fontSize: 12,
                                                    marginTop: 4
                                                }}
                                            >
                                                {fieldErrors.city}
                                            </div>
                                        )}
                                    </Row>

                                    <Row label="Time Zone" NonMandatory>
                                        <Select
                                            options={timeZoneOptions}
                                            value={selectedTimeZone}
                                            onChange={(e) => {
                                                const val = e?.label || "";
                                                setForm1({
                                                    ...form1,
                                                    timeZone: val
                                                });
                                                SetSelectedTimeZone(e);
                                                runFieldValidation(
                                                    val
                                                );
                                            }}
                                            placeholder="Search"
                                            isSearchable
                                            styles={{
                                                control: (b) => ({
                                                    ...b,
                                                    minHeight: 20,
                                                    fontSize: 14
                                                }),
                                                dropdownIndicator: (b) => ({
                                                    ...b,
                                                    padding: 2
                                                }),
                                                clearIndicator: (b) => ({
                                                    ...b,
                                                    padding: 2
                                                }),
                                                valueContainer: (b) => ({
                                                    ...b,
                                                    padding: "0 6px"
                                                }),
                                                input: (b) => ({
                                                    ...b,
                                                    margin: 0,
                                                    padding: 0
                                                })
                                            }}
                                        />
                                        {fieldErrors.timeZone && (
                                            <div
                                                style={{
                                                    color: "red",
                                                    fontSize: 12,
                                                    marginTop: 4
                                                }}
                                            >
                                                {fieldErrors.timeZone}
                                            </div>
                                        )}
                                    </Row>

                                    <Row label="Occupancy (Virtual/Office)">
                                        <select
                                            style={{ width: "108%" }}
                                            value={form1.occupancyCode}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                setForm1({
                                                    ...form1,
                                                    occupancyCode: v
                                                });
                                                runFieldValidation(
                                                    "occupancyCode",
                                                    v
                                                );
                                            }}
                                        >
                                            <option value="">Select</option>
                                            {dd.occupancyCode.map((t) => (
                                                <option key={t} value={t}>
                                                    {t}
                                                </option>
                                            ))}
                                        </select>
                                        {fieldErrors.occupancyCode && (
                                            <div
                                                style={{
                                                    color: "red",
                                                    fontSize: 12,
                                                    marginTop: 4
                                                }}
                                            >
                                                {fieldErrors.occupancyCode}
                                            </div>
                                        )}
                                    </Row>

                                    <div
                                        className="row"
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            columnGap: "12px",
                                            flexWrap: "nowrap",
                                            whiteSpace: "nowrap"
                                        }}
                                    >
                                        <label
                                            style={{
                                                marginBottom: 0,
                                                marginRight: 4,
                                                whiteSpace: "nowrap"
                                            }}
                                        >
                                            Attuid Required?
                                        </label>
                                        <div className="toggle-wrap">
                                            <button
                                                type="button"
                                                className={`toggle-switch ${attuidRequiredOn
                                                    ? "on"
                                                    : "off"
                                                    }`}
                                                onClick={handleAttuidToggle}
                                                aria-pressed={attuidRequiredOn}
                                            >
                                                <span className="toggle-knob" />
                                            </button>
                                            <span className="toggle-label">
                                                {attuidRequiredOn ? "Yes" : "No"}
                                            </span>
                                        </div>
                                    </div>



                                    {attuidRequiredOn && (


                                        <Row
                                            label="Role (Title)"
                                            NonMandatory={NonMandatory.includes(
                                                "role"
                                            )}
                                        >
                                            <Select
                                                options={dd.roles.map((r) => ({
                                                    label: r,
                                                    value: r
                                                }))}
                                                value={
                                                    form1.role
                                                        ? {
                                                            label: form1.role,
                                                            value: form1.role
                                                        }
                                                        : null
                                                }

                                                onChange={(opt) => {
                                                    const val = opt?.value || "";
                                                    // Prepare base next state
                                                    let next = { ...form1, role: val };
                                                    // If attuid is required, try to auto-select ATT role tied to this DTV role
                                                    if (attuidRequiredOn) {
                                                        const auto = autoSelectAttRoleForDtvRole(val);
                                                        if (auto) {
                                                            next = { ...next, ...auto };
                                                        }
                                                    }
                                                    setForm1(next);
                                                    runFieldValidation("role", val);
                                                }}

                                                placeholder="Select"
                                                isClearable
                                                isSearchable
                                                styles={{
                                                    control: (b) => ({
                                                        ...b,
                                                        minHeight: 20,
                                                        fontSize: 14
                                                    }),
                                                    dropdownIndicator: (b) => ({
                                                        ...b,
                                                        padding: 2
                                                    }),
                                                    clearIndicator: (b) => ({
                                                        ...b,
                                                        padding: 2
                                                    }),
                                                    valueContainer: (b) => ({
                                                        ...b,
                                                        padding: "0 6px"
                                                    }),
                                                    input: (b) => ({
                                                        ...b,
                                                        margin: 0,
                                                        padding: 0
                                                    })
                                                }}
                                            />
                                            {fieldErrors.role && (
                                                <div
                                                    style={{
                                                        color: "red",
                                                        fontSize: 12,
                                                        marginTop: 4
                                                    }}
                                                >
                                                    {fieldErrors.role}
                                                </div>
                                            )}
                                        </Row>
                                    )}

                                    {attuidRequiredOn && (
                                        <>
                                            <Row label="W2 / 1099 or International" NonMandatory={false}>
                                                <select
                                                    style={{ width: "108%" }}
                                                    value={form1.workerTypeTax}
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        setForm1({
                                                            ...form1,
                                                            workerTypeTax: v
                                                        });
                                                        runFieldValidation(
                                                            "workerTypeTax",
                                                            v
                                                        );
                                                    }}
                                                    disabled={false}
                                                >
                                                    <option value="">Select</option>
                                                    {dd.workerTypeTax.map((t) => (
                                                        <option key={t} value={t}>
                                                            {t}
                                                        </option>
                                                    ))}
                                                </select>
                                            </Row>
                                            {fieldErrors.workerTypeTax && (
                                                <div
                                                    style={{
                                                        color: "red",
                                                        fontSize: 12,
                                                        marginTop: 4
                                                    }}
                                                >
                                                    {fieldErrors.workerTypeTax}
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {/* {attuidRequiredOn && (
                                        <Row label="ATT Role" NonMandatory>
                                            <div className="field-hint" style={{ fontSize: 12, marginTop: 2, color: "#555" }}>
                                                ATT Role is auto-selected based on the chosen Role.
                                            </div>
                                        </Row>
                                    )} */}
                                </Section>
                            </div>

                            <div>
                                <Section title="Contact & Assignment Details:">
                                    <TextRow
                                        label="Contract #"
                                        NonMandatory
                                        v={form1.contractPo}
                                        maxLength={CONTRACT_FIELDS_MAX_LEN}
                                        onChange={(v) => {
                                            const next = String(v || "").slice(0, CONTRACT_FIELDS_MAX_LEN);
                                            setForm1({
                                                ...form1,
                                                contractPo: next
                                            });
                                            runFieldValidation(
                                                "contractPo",
                                                next
                                            );
                                        }}
                                    />
                                    {(contractValidation.status !== "idle") && (
                                        <div
                                            className="field-hint"
                                            style={{
                                                fontSize: 12,
                                                marginTop: 4,
                                                color: contractValidation.status === "error" ? "#b45309" : contractValidation.status === "ok" ? "green" : "#555"
                                            }}
                                        >
                                            {contractValidation.status === "checking" && "Validating contract..."}
                                            {contractValidation.status === "ok" && "Contract verified."}
                                            {contractValidation.status === "error" && (contractValidation.message || "Contract not valid")}
                                        </div>
                                    )}
                                    {fieldErrors.contractPo && (
                                        <div
                                            style={{
                                                color: "red",
                                                fontSize: 12,
                                                marginTop: 4
                                            }}
                                        >
                                            {fieldErrors.contractPo}
                                        </div>
                                    )}
                                    <TextRow
                                        label="PO #"
                                        NonMandatory
                                        v={form1.poNum}
                                        maxLength={CONTRACT_FIELDS_MAX_LEN}
                                        onChange={(v) => {
                                            const next = String(v || "").slice(0, CONTRACT_FIELDS_MAX_LEN);
                                            setForm1({
                                                ...form1,
                                                poNum: next
                                            });
                                            runFieldValidation("poNum", next);
                                        }}
                                    />
                                    {(poValidation.status !== "idle") && (
                                        <div
                                            className="field-hint"
                                            style={{
                                                fontSize: 12,
                                                marginTop: 4,
                                                color: poValidation.status === "error" ? "#b45309" : poValidation.status === "ok" ? "green" : "#555"
                                            }}
                                        >
                                            {poValidation.status === "checking" && "Validating purchase order..."}
                                            {poValidation.status === "ok" && "Purchase order verified."}
                                            {poValidation.status === "error" && (poValidation.message || "Purchase Order not valid")}
                                        </div>
                                    )}
                                    {fieldErrors.poNum && (
                                        <div
                                            style={{
                                                color: "red",
                                                fontSize: 12,
                                                marginTop: 4
                                            }}
                                        >
                                            {fieldErrors.poNum}
                                        </div>
                                    )}
                                    <TextRow
                                        label="Line #"
                                        NonMandatory
                                        v={form1.lineNum}
                                        maxLength={CONTRACT_FIELDS_MAX_LEN}
                                        onChange={(v) => {
                                            const digits = v.replace(/\D/g, "").slice(0, CONTRACT_FIELDS_MAX_LEN);
                                            setForm1({
                                                ...form1,
                                                lineNum: digits
                                            });
                                            runFieldValidation(
                                                "lineNum",
                                                digits
                                            );
                                        }}
                                    />
                                    {(lineValidation.status !== "idle") && (
                                        <div
                                            className="field-hint"
                                            style={{
                                                fontSize: 12,
                                                marginTop: 4,
                                                color: lineValidation.status === "error" ? "#b45309" : lineValidation.status === "ok" ? "green" : "#555"
                                            }}
                                        >
                                            {lineValidation.status === "checking" && "Validating PO line..."}
                                            {lineValidation.status === "ok" && "PO line verified."}
                                            {lineValidation.status === "error" && (lineValidation.message || "PO Line not valid")}
                                        </div>
                                    )}
                                    {fieldErrors.lineNum && (
                                        <div
                                            style={{
                                                color: "red",
                                                fontSize: 12,
                                                marginTop: 4
                                            }}
                                        >
                                            {fieldErrors.lineNum}
                                        </div>
                                    )}

                                    <Row label="Start Date">
                                        <DatePicker
                                            className="input-styles"
                                            selected={form1.startDate}
                                            onChange={(date) => {
                                                setForm1((prev) => ({
                                                    ...prev,
                                                    startDate: date,
                                                    endDate:
                                                        prev.endDate &&
                                                            date &&
                                                            prev.endDate < date
                                                            ? null
                                                            : prev.endDate
                                                }));
                                                runFieldValidation(
                                                    "startDate",
                                                    date
                                                );
                                            }}
                                            showYearDropdown
                                            scrollableYearDropdown
                                            dateFormat="MM/dd/yyyy"
                                            minDate={minStartDate}
                                            maxDate={maxStartDate}
                                            popperPlacement="bottom-start"
                                            portalId="root"
                                            calendarClassName="custom-calendar"
                                        />
                                        {fieldErrors.startDate && (
                                            <div
                                                style={{
                                                    color: "red",
                                                    fontSize: 12,
                                                    marginTop: 4
                                                }}
                                            >
                                                {fieldErrors.startDate}
                                            </div>
                                        )}
                                    </Row>

                                    <Row label="End Date">
                                        <DatePicker
                                            className="input-styles"
                                            selected={form1.endDate}
                                            onChange={(date) => {
                                                setForm1({
                                                    ...form1,
                                                    endDate: date
                                                });
                                                runFieldValidation(
                                                    "endDate",
                                                    date
                                                );
                                            }}
                                            showYearDropdown
                                            scrollableYearDropdown
                                            dateFormat="MM/dd/yyyy"
                                            disabled={!form1.startDate}
                                            minDate={minEndDate}
                                            popperPlacement="bottom-start"
                                            portalId="root"
                                            calendarClassName="custom-calendar"
                                        />
                                        {fieldErrors.endDate && (
                                            <div
                                                style={{
                                                    color: "red",
                                                    fontSize: 12,
                                                    marginTop: 4
                                                }}
                                            >
                                                {fieldErrors.endDate}
                                            </div>
                                        )}
                                    </Row>

                                    {/* SECURITY ID (masked + eye inside input) */}
                                    <Row
                                        NonMandatory={isSecurityLocked}
                                        label={
                                            <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                                                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                                    <span>{securityLabel}</span>
                                                    <button
                                                        type="button"
                                                        aria-label="Security ID / SSN help"
                                                        title={securityTooltipText}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: 20,
                                                            height: 20,
                                                            borderRadius: 999,
                                                            background: "#fffbeb",
                                                            border: "1px solid #fcd34d",
                                                            color: "#b45309",
                                                            cursor: "help",
                                                            padding: 0,
                                                            boxShadow: "0 1px 0 rgba(0,0,0,0.04)"
                                                        }}
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                            <path d="M12 17v-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                            <path d="M12 8h.01" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                                                            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                                                        </svg>
                                                    </button>
                                                </span>
                                                {showSecurityEditButton && (
                                                    <button
                                                        type="button"
                                                        onClick={handleEnableSecurityEdit}
                                                        style={{
                                                            border: "1px solid #cbd5e1",
                                                            background: "#fff",
                                                            color: "#0f172a",
                                                            padding: "2px 10px",
                                                            borderRadius: 6,
                                                            cursor: "pointer",
                                                            fontSize: 12,
                                                            lineHeight: "20px"
                                                        }}
                                                    >
                                                        Edit
                                                    </button>
                                                )}
                                                {showSecurityDisableEditButton && (
                                                    <button
                                                        type="button"
                                                        onClick={handleDisableSecurityEdit}
                                                        style={{
                                                            border: "1px solid #cbd5e1",
                                                            background: "#fff",
                                                            color: "#0f172a",
                                                            padding: "2px 10px",
                                                            borderRadius: 6,
                                                            cursor: "pointer",
                                                            fontSize: 12,
                                                            lineHeight: "20px"
                                                        }}
                                                    >
                                                        Disable
                                                    </button>
                                                )}
                                            </span>
                                        }
                                    >
                                        <div style={{ display: "flex", flexDirection: "column", gap: 4, width: SECURITY_INPUT_WIDTH_PX }}>
                                            <div style={{ position: "relative", display: "inline-block", width: SECURITY_INPUT_WIDTH_PX }}>
                                                <input
                                                    className="input-styles"
                                                    ref={securityRef}
                                                    value={securityDisplayValue}
                                                    placeholder={securityPlaceholder}
                                                    inputMode="numeric"
                                                    autoComplete="off"

                                                    readOnly={isSecurityLocked}
                                                    title={isSecurityLocked ? "Click Edit to update Security ID" : ""}
                                                    onChange={handleSecChange("securityId")}
                                                    onKeyDown={handleSecKeyDown("securityId")}
                                                    onPaste={handleSecPaste("securityId")}
                                                    onFocus={handleSecFocus("securityId")}
                                                    onBlur={() => { if (!isSecurityLocked) runFieldValidation("securityId", form1.securityId); }}
                                                    style={{ paddingRight: 48, width: SECURITY_INPUT_WIDTH_PX, boxSizing: "border-box", backgroundColor: isSecurityLocked ? "#e5e7eb" : undefined, cursor: isSecurityLocked ? "not-allowed" : undefined, opacity: isSecurityLocked ? 0.9 : 1 }}
                                                />
                                                <button
                                                    type="button"

                                                    aria-label={showSecurityId ? "Hide Security ID" : "Show Security ID"}
                                                    title={showSecurityId ? "Hide" : "Show"}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={() => {
                                                        setShowSecurityId((p) => !p);
                                                        if (!isSecurityLocked) {
                                                            queueMicrotask(() => securityRef.current?.focus());
                                                        }
                                                    }}
                                                    style={{
                                                        position: "absolute",
                                                        right: 8,
                                                        top: "50%",
                                                        transform: "translateY(-50%)",
                                                        height: 34,
                                                        width: 34,
                                                        border: 0,
                                                        background: "transparent",
                                                        cursor: "pointer",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        color: "#334155",
                                                        padding: 0,
                                                    }}
                                                >
                                                    <EyeIcon />
                                                </button>
                                            </div>

                                            <div style={{ fontSize: 12, color: "#64748b", width: SECURITY_INPUT_WIDTH_PX, wordBreak: "break-word", overflowWrap: "anywhere" }}>{securityHint}</div>

                                            {ssnAssociation?.status === "checking" && (
                                                <div style={{ fontSize: 12, color: "#64748b", width: SECURITY_INPUT_WIDTH_PX, wordBreak: "break-word", overflowWrap: "anywhere" }}>
                                                    {`Checking ${securityLabel}…`}
                                                </div>
                                            )}
                                            {ssnAssociation?.status === "found" && ssnAssociation?.message && (
                                                <div style={{ fontSize: 12, color: "#b45309", marginTop: 2, width: SECURITY_INPUT_WIDTH_PX, wordBreak: "break-word", overflowWrap: "anywhere" }}>
                                                    {ssnAssociation.message}
                                                </div>
                                            )}

                                            {ssnAssociation?.status === "none" && ssnAssociation?.message && (
                                                <div style={{ fontSize: 12, color: "#16a34a", marginTop: 2, width: SECURITY_INPUT_WIDTH_PX, wordBreak: "break-word", overflowWrap: "anywhere" }}>
                                                    {ssnAssociation.message}
                                                </div>
                                            )}


                                            {fieldErrors.securityId && (
                                                <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                                                    {fieldErrors.securityId}
                                                </div>
                                            )}
                                        </div>
                                    </Row>

                                    {/* CONFIRM SECURITY ID (masked + eye inside input) */}
                                    <Row NonMandatory={isSecurityLocked} label={confirmSecurityLabel}>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 4, width: SECURITY_INPUT_WIDTH_PX }}>
                                            <div style={{ position: "relative", display: "inline-block", width: SECURITY_INPUT_WIDTH_PX }}>
                                                <input
                                                    className="input-styles"
                                                    ref={confirmSecurityRef}
                                                    value={confirmDisplayValue}
                                                    placeholder={securityPlaceholder}
                                                    inputMode="numeric"
                                                    autoComplete="off"

                                                    readOnly={isSecurityLocked}
                                                    title={isSecurityLocked ? "Click Edit to update Security ID" : ""}
                                                    onChange={handleSecChange("validateSecurity")}
                                                    onKeyDown={handleSecKeyDown("validateSecurity")}
                                                    onPaste={handleSecPaste("validateSecurity")}
                                                    onFocus={handleSecFocus("validateSecurity")}
                                                    onBlur={() => { if (!isSecurityLocked) runFieldValidation("validateSecurity", form1.validateSecurity); }}
                                                    style={{ paddingRight: 48, width: SECURITY_INPUT_WIDTH_PX, boxSizing: "border-box", backgroundColor: isSecurityLocked ? "#e5e7eb" : undefined, cursor: isSecurityLocked ? "not-allowed" : undefined, opacity: isSecurityLocked ? 0.9 : 1 }}
                                                />
                                                <button
                                                    type="button"

                                                    aria-label={showConfirmSecurityId ? "Hide Confirm Security ID" : "Show Confirm Security ID"}
                                                    title={showConfirmSecurityId ? "Hide" : "Show"}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={() => {
                                                        setShowConfirmSecurityId((p) => !p);
                                                        if (!isSecurityLocked) {
                                                            queueMicrotask(() => confirmSecurityRef.current?.focus());
                                                        }
                                                    }}
                                                    style={{
                                                        position: "absolute",
                                                        right: 8,
                                                        top: "50%",
                                                        transform: "translateY(-50%)",
                                                        height: 34,
                                                        width: 34,
                                                        border: 0,
                                                        background: "transparent",
                                                        cursor: "pointer",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        color: "#334155",
                                                        padding: 0,
                                                    }}
                                                >
                                                    <EyeIcon />
                                                </button>
                                            </div>

                                            <div style={{ fontSize: 12, color: "#64748b", width: SECURITY_INPUT_WIDTH_PX, wordBreak: "break-word", overflowWrap: "anywhere" }}>{securityHint}</div>

                                            {fieldErrors.validateSecurity && (
                                                <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                                                    {fieldErrors.validateSecurity}
                                                </div>
                                            )}
                                        </div>
                                    </Row>



                                    <Row
                                        label="Billable (Yes / No)"
                                        NonMandatory={NonMandatory.includes("nonBillable")}
                                    >
                                        <select
                                            style={{ width: "108%" }}
                                            value={form1.nonBillable}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                setForm1({
                                                    ...form1,
                                                    nonBillable: v
                                                });
                                                runFieldValidation(
                                                    "nonBillable",
                                                    v
                                                );
                                            }}
                                            disabled={false}
                                        >
                                            <option value="">Select</option>
                                            {dd.nonBillable.map((s) => (
                                                <option key={s} value={s}>
                                                    {s}
                                                </option>
                                            ))}
                                        </select>
                                    </Row>
                                    {fieldErrors.nonBillable && (
                                        <div
                                            style={{
                                                color: "red",
                                                fontSize: 12,
                                                marginTop: 4
                                            }}
                                        >
                                            {fieldErrors.nonBillable}
                                        </div>
                                    )}
                                </Section>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div
                            className="review"
                            style={{
                                padding: "32px 40px 40px",
                                width: "100%",
                                maxWidth: "1800px",
                                margin: "0 auto"
                            }}
                        >
                            <div
                                className="grid-3"
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                                    columnGap: "25px",
                                    rowGap: "18px",
                                    alignItems: "flex-start"
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "24px"
                                    }}
                                >
                                    <Overview
                                        setStep={setStep}
                                        title="Personal Details:"
                                        form={1}
                                        items={{
                                            "First Name": form1.firstName,
                                            "Last Name": form1.lastName,
                                            Email: form1.email,
                                            Phone: form1.phone,
                                            ...((effectiveDtvid && String(effectiveDtvid).trim()) && !(effectiveNpwId && String(effectiveNpwId).trim())
                                                ? {
                                                    ...(String(form1?.dtvId || editData?.dtvId || editData?.dtv_id || editData?.dtvAnchorId || "").trim()
                                                        ? { DTVID: String(form1?.dtvId || editData?.dtvId || editData?.dtv_id || editData?.dtvAnchorId || "").trim() }
                                                        : {}),
                                                    ...(String(form1?.attuid || editData?.attuid || editData?.attUid || editData?.attUID || editData?.att_uid || "").trim()
                                                        ? { ATTUID: String(form1?.attuid || editData?.attuid || editData?.attUid || editData?.attUID || editData?.att_uid || "").trim() }
                                                        : {})
                                                }
                                                : {}),
                                            ...(attuidRequiredOn
                                                ? {
                                                    "Country of Citizenship":
                                                        form1.citizenship
                                                }
                                                : {})
                                        }}
                                    />

                                    <Overview
                                        setStep={setStep}
                                        title="Project Details:"
                                        form={1}
                                        items={{
                                            "Supplier Reference":
                                                form1.supplierReference,
                                            Vendor: form1.vendor
                                        }}
                                    />
                                </div>

                                <Overview
                                    setStep={setStep}
                                    title="Sponsorship & Role:"
                                    form={1}
                                    items={{
                                        // Sponsor: form1.sponsor,
                                        "DTV Sponsor DTVUID": form1.sponsor,
                                        //  "Webphone Manager": form1.webphoneManagerId,
                                        "Webphone Manager DTVUID": form1.webphoneManagerId,
                                        Role: form1.role,
                                        Country: form1.country,
                                        State: form1.state,
                                        City: form1.city,
                                        "Time Zone": form1.timeZone,
                                        Occupancy: form1.occupancyCode,
                                        "Attuid Required":
                                            form1.attuidRequired,
                                        ...(attuidRequiredOn
                                            ? {
                                                "W2 / 1099 or International":
                                                    form1.workerTypeTax
                                            }
                                            : {}),
                                        ...(attuidRequiredOn &&
                                            form1.attRoleCpRoleId
                                            ? {
                                                "ATT Role": `${form1.attRoleCpRoleId
                                                    } - ${form1.attRoleCpProfileDesc ||
                                                    ""
                                                    }`
                                            }
                                            : {})
                                    }}
                                />

                                <Overview
                                    setStep={setStep}
                                    title="Contact & Assignment Details:"
                                    form={1}
                                    items={{
                                        "Contract #": form1.contractPo,
                                        "PO #": form1.poNum,
                                        "Line #": form1.lineNum,
                                        "Start Date":
                                            form1.startDate &&
                                            fmtDate(form1.startDate),
                                        "End Date":
                                            form1.endDate &&
                                            fmtDate(form1.endDate),
                                        "Security ID": form1.securityId ? buildMaskedLast4(isUS, clampDigitsToFormat(isUS, extractDigits(form1.securityId, isUS))) : "-",
                                        // "Confirm Security ID": form1.validateSecurity ? buildMaskedLast4(isUS, clampDigitsToFormat(isUS, extractDigits(form1.validateSecurity, isUS))) : "-",

                                        "Billable": form1.nonBillable
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    <div
                        className="onb-actions"
                        style={{ paddingRight: "100px" }}
                    >
                        <div
                            className="right"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px"
                            }}
                        >
                            {step > 1 ? (
                                <button
                                    className="button back"
                                    onClick={() => {
                                        if (isEditRoute && hasExternalCallerTarget) {
                                            handleBackToCaller();
                                            return;
                                        }
                                        setStep(step - 1);
                                    }}
                                >
                                    ≪ Back
                                </button>
                            ) : (
                                <>
                                    <span />
                                    {Boolean((effectiveDtvid && String(effectiveDtvid).trim()) && !(effectiveNpwId && String(effectiveNpwId).trim())) && (
                                        <button
                                            className="button back"
                                            onClick={handleBackToCaller}
                                        >
                                            <span style={{ color: "grey" }}>
                                                {" "}
                                                «
                                            </span>{" "}
                                            Back
                                        </button>
                                    )}
                                </>
                            )}
                            <button
                                className="button back"
                                onClick={handleCancel}
                            >
                                Cancel
                            </button>
                            {(editData === null ||
                                editData?.status === "Draft") && (
                                    <button
                                        className="button back"
                                        onClick={saveDraft}
                                        disabled={loading}
                                    >
                                        Save Draft
                                    </button>
                                )}
                            {step < 2 ? (
                                <button
                                    className="btn primary"
                                    onClick={handleContinue}
                                >
                                    Continue ≫
                                </button>
                            ) : (
                                <>
                                    {requiresCpValidateBeforeSubmit && (
                                        <button
                                            className="button back"
                                            onClick={validateBeforeSubmit}
                                            disabled={loading || submitting || cpValidation.status === "checking"}
                                        >
                                            {cpValidation.status === "checking" ? "Validating..." : "Validate"}
                                        </button>
                                    )}
                                    <button
                                        className="btn primary"
                                        onClick={submitAll}
                                        disabled={submitting || loading || (requiresCpValidateBeforeSubmit && cpValidation.status !== "passed")}
                                        title={requiresCpValidateBeforeSubmit && cpValidation.status !== "passed" ? "Run Validate first" : undefined}
                                    >
                                        {submitting ? "Submitting..." : "Submit"}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </AppShell>
    );
}

function Section({ title, children, style = false }) {
    return (
        <div style={style !== false ? style : {}} className="onb-section">
            <div className="onb-section-title">
                {title}
                <hr style={{ border: "none", borderTop: "1px solid #ddd" }} />
            </div>
            <div className="onb-section-body">{children}</div>
        </div>
    );
}

function Row({ label, children, rdOnly, NonMandatory = false, inline = false }) {
    return (
        <div className={`row ${inline ? "row-inline" : ""}`}>
            <label>
                <span style={{ display: "inline-flex", alignItems: "center", width: "100%" }}>
                    {!rdOnly && !NonMandatory && (
                        <span style={{ marginRight: 4 }}>*</span>
                    )}
                    <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
                </span>
            </label>
            {!rdOnly && !inline && <br />}
            {children}
        </div>
    );
}

function TextRow({
    label,
    v,
    onChange,
    rdOnly = false,
    NonMandatory = false,
    disabled = false,
    inline = false,
    error = false,
    errorLabel = null,
    maxLength = undefined
}) {
    const inlineInputStyle = inline ? { marginLeft: "-40px", width: "45%" } : {};
    const inlineTextStyle = inline ? { marginLeft: "-100px" } : {};
    const errorStyle = error ? { borderColor: "red" } : {};
    return (
        <Row
            label={label}
            NonMandatory={NonMandatory}
            rdOnly={rdOnly}
            inline={inline}
        >
            {rdOnly ? (
                <b style={inlineTextStyle}>{v}</b>
            ) : (
                <input
                    readOnly={rdOnly}
                    value={v || ""}
                    disabled={disabled}
                    maxLength={maxLength}
                    style={{ ...inlineInputStyle, ...errorStyle }}
                    onChange={(e) => onChange(rdOnly ? v : e.target.value)}
                />
            )}
            {errorLabel || null}
        </Row>
    );
}

function Overview({ title, items, setStep, form, style = false }) {
    // NOTE: Per requirement, label is driven only by ATTUID Required + Country.
    // We intentionally keep this logic inside the Overview component so onboarding form logic stays unchanged.
    const getItemValue = (obj, keyRegex) => {
        if (!obj || typeof obj !== "object") return undefined;
        for (const [k, v] of Object.entries(obj)) {
            if (keyRegex.test(String(k || ""))) return v;
        }
        return undefined;
    };
    const isTruthyYes = (v) => {
        if (v === true) return true;
        const s = String(v ?? "").trim().toLowerCase();
        return s === "yes" || s === "true" || s === "1" || s === "y";
    };
    const isUnitedStatesish = (v) => {
        const s0 = String(v ?? "").trim().toLowerCase();
        if (!s0) return false;
        const s = s0.replace(/[.()]/g, "").replace(/\s+/g, " ").trim();
        if (s === "us" || s === "u s" || s === "usa") return true;
        return s.includes("united states");
    };

    // Persist the latest context we can infer from Overview items (review step renders multiple Overview cards).
    // This allows Contact & Assignment card to use the context derived from Sponsorship & Role card.
    // (No changes to caller props needed.)
    if (!window.__npwOverviewCtx) window.__npwOverviewCtx = { attuidOn: false, isUS: false };
    try {
        const attuidVal = getItemValue(items, /^\s*attuid\s*required\s*\??\s*$/i);
        const countryVal = getItemValue(items, /^\s*country\s*$/i);
        if (attuidVal !== undefined) window.__npwOverviewCtx.attuidOn = isTruthyYes(attuidVal);
        if (countryVal !== undefined) window.__npwOverviewCtx.isUS = isUnitedStatesish(countryVal);
    } catch { /* ignore */ }

    const baseSecurityLabel = (window.__npwOverviewCtx?.attuidOn && window.__npwOverviewCtx?.isUS)
        ? "SSN #"
        : "Security ID #";
    const normalizeOverviewLabel = (raw) => {
        const s = String(raw || "").trim();
        if (!s) return s;
        if (/^confirm\s+(?:security\s*id|ssn)\b/i.test(s)) return `Confirm ${baseSecurityLabel}`;
        if (/^(?:security\s*id|ssn)\b/i.test(s)) return baseSecurityLabel;
        return s;
    };

    const normalizeOverviewValue = (rawKey, rawValue) => {
        const raw = String(rawKey || "");
        const normalizedKey = normalizeOverviewLabel(raw);

        const isSecurityKey =
            /(?:^|\b)(?:confirm\s+)?(?:security\s*id|ssn)\b/i.test(raw) ||
            /(?:^|\b)(?:confirm\s+)?(?:security\s*id|ssn)\b/i.test(normalizedKey);

        const isRoleKey = /\brole\b/i.test(raw) || /\brole\b/i.test(normalizedKey);
        const attuidOn = Boolean(window.__npwOverviewCtx?.attuidOn);

        if (isRoleKey) {
            if (!attuidOn) return null;
            const v = rawValue == null ? "" : String(rawValue).trim();
            if (!v || v === "-") return null;
            if (/^fte\s*nominator$/i.test(v)) return null;
            return v;
        }

        if (!isSecurityKey) {
            if (rawValue == null) return null;
            const s = String(rawValue).trim();
            if (!s || s === "-") return null;
            return s;
        }

        const s = rawValue == null ? "" : String(rawValue).trim();
        if (!s || s === "-" || /^[*]+$/.test(s)) return "********";
        return s;
    };

    const LABEL_FONT_SIZE = 12;
    const VALUE_FONT_SIZE = 12;
    const GRID_GAP = 10;
    const CARD_PADDING = "20px 20px";
    const CARD_WIDTH = "100%";
    const LABEL_COLOR = "#333";
    const VALUE_COLOR = "#000";
    const LENGTH_THRESHOLD = 30;
    const FORCE_NEXT_LINE_KEYS = ["Email", "Sponsor", "Webphone Manager", "Time Zone"];
    const cardStyle = {
        padding: CARD_PADDING,
        borderRadius: "10px",
        border: "1px solid #e3e3e3",
        background: "#ffffff",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
        width: CARD_WIDTH
    };
    return (
        <div
            className="onb-section"
            style={style !== false ? { ...cardStyle, ...style } : cardStyle}
        >
            <div
                className="onb-section-title"
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 12,
                    fontSize: LABEL_FONT_SIZE + 2
                }}
            >
                <div>{title}</div>
                <span className="edit-pointer" onClick={() => setStep(form)}>
                    <svg
                        viewBox="0 0 24 24"
                        width="20"
                        height="20"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                    </svg>
                </span>
            </div>

            <hr
                style={{
                    border: "none",
                    borderTop: "1px solid #ddd",
                    margin: "0 0 12px"
                }}
            />

            <div
                className="overview-list"
                style={{
                    display: "grid",
                    rowGap: GRID_GAP
                }}
            >
                {Object.entries(items).map(([k, v]) => {
                    const rawLabel = k || "";
                    const label = normalizeOverviewLabel(rawLabel);
                    const displayValue = normalizeOverviewValue(rawLabel, v);
                    if (displayValue === null) return null;
                    const valueStr = String(displayValue);
                    const combinedLength = label.length + valueStr.length;
                    const forceNextLine = FORCE_NEXT_LINE_KEYS.includes(label);
                    const valueOnNextLine =
                        forceNextLine || combinedLength > LENGTH_THRESHOLD;
                    return (
                        <div
                            className="ov-row"
                            key={rawLabel}
                            style={{
                                // display: "grid",
                                // gridTemplateColumns: valueOnNextLine
                                //     ? "1fr"
                                //     : "max-content 1fr",
                                display: "flex",
                                flexWrap: "wrap",
                                columnGap: 10,
                                alignItems: "start"
                            }}
                        >
                            <span
                                style={{
                                    color: LABEL_COLOR,
                                    fontWeight: 400,
                                    fontSize: LABEL_FONT_SIZE,
                                    whiteSpace: "nowrap"
                                }}
                            >
                                {label}:
                            </span>
                            <span
                                style={{
                                    color: VALUE_COLOR,
                                    fontWeight: 700,
                                    fontSize: VALUE_FONT_SIZE,
                                    whiteSpace: valueOnNextLine ? "nowrap" : "normal",
                                    // whiteSpace: "normal",
                                    // wordBreak: "break-word",
                                    // overflowWrap: "anywhere",
                                    // display: valueOnNextLine ? "block" : "inline"
                                }}
                            >
                                {valueStr}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div >
    );
}

function StepDot({ n, label, active, done }) {
    return (
        <div className="step-dot">
            <div
                className={`dot ${active ? "active" : ""} ${done ? "done" : ""
                    }`}
            >
                {n}
            </div>
            <div className="dot-label">{label}</div>
        </div>
    );
}

function StepBar({ done }) {
    return <div className={`step-bar ${done ? "done" : ""}`} />;
}